import { useCallback, useEffect, useMemo, useRef, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { useAuth } from "../../auth/authContext.ts";
import {
  actualizarPersona,
  apiConfigurada,
  crearPersona,
  expedienteEstado,
  guardarGrafoBc,
  listarCatalogo,
  listarPersonas,
  normalizarRelacionPep,
  obtenerGrafoBc,
  subirDocumentoExpediente,
  type BcArista,
  type BcBeneficiario,
  type BcNodo,
  type CamposRelacionPep,
  type CatalogoRow,
  type PersonaRow,
  type RelacionPep,
  type RequisitoEstado,
  type TipoControlBc,
  type TipoPersona,
} from "../../lib/api.ts";
import { C, st, fecha } from "../../ui/theme.ts";
import { Chip } from "../../ui/componentes.tsx";
import { IconoPersonas } from "../../ui/iconos.tsx";
import { nombrePersona } from "../../ui/persona.ts";
import { armarDomicilioPersona, erroresDomicilioPersona } from "./personaDomicilio.ts";

/**
 * PERSONAS — rebanada 1 de la beta (IA: lista central + detalle/captura).
 *
 *  · Lista con búsqueda y alta ("los expedientes se capturan UNA vez y se
 *    referencian desde captación y compraventas").
 *  · Detalle: datos por tipo + EXPEDIENTE POR REQUISITOS (0021): el cuestionario
 *    condicionado viene resuelto de expediente_estado(); subir un documento
 *    calcula su SHA-256 en el navegador, lo guarda en Storage y liga la fila en
 *    `documento`; la completitud la deriva el backend, no un checkbox.
 *  · Sin backend configurado corre en MODO DEMOSTRACIÓN (datos en memoria, el
 *    mismo contrato de datos) para revisar la UX con `pnpm dev`.
 */

const OPACIDAD_OCUPADO = 0.6;
const OPACIDAD_GUARDANDO = 0.7;
const LONGITUD_ACTIVIDAD_GIRO = 7;
const LONGITUD_RFC_FISICA = 13;
const LONGITUD_RFC_MORAL = 12;
const LONGITUD_CURP = 18;
const LONGITUD_PAIS = 2;
const LONGITUD_CP = 5;
const LONGITUD_TELEFONO = 12;
const RFC_FISICA_RE = /^[A-ZÑ&%+]{4}\d{6}[A-Z0-9]{3}$/;
const RFC_MORAL_RE = /^[A-ZÑ&%+]{3}\d{6}[A-Z0-9]{3}$/;
const CURP_RE = /^[A-Z]{4}\d{6}[MH][A-Z]{5}[0-9]{2}$/;
const PAIS_RE = /^[A-Z]{2}$/;
const TELEFONO_RE = /^\d{10,12}$/;
const CORREO_RE = /^[A-Z\d._'-]+@[A-Z\d_'-]+\.[A-Z\d._'-]+$/;

// ─────────────────────────────────────────────────────────────────────────────
// Mensajes de validación por campo — específicos y accionables (con ejemplo).
// El regex es la regla; estos textos explican QUÉ campo y QUÉ estructura espera,
// para que el usuario no se quede con "estructura válida" sin saber qué corregir.
// ─────────────────────────────────────────────────────────────────────────────

const EJEMPLO_RFC_MORAL = "SCO230415AB9";
const EJEMPLO_RFC_FISICA = "GAGB880430KP1";
const MSG_RFC_MORAL = `RFC de persona moral: 3 letras + 6 dígitos de fecha de constitución (AAMMDD) + 3 de homoclave. Ej: ${EJEMPLO_RFC_MORAL}`;
const MSG_RFC_FISICA = `RFC de persona física: 4 letras + 6 dígitos de fecha de nacimiento (AAMMDD) + 3 de homoclave. Ej: ${EJEMPLO_RFC_FISICA}`;
const MSG_RFC_FIDEICOMISO = `RFC del fideicomiso: 12 o 13 caracteres (letras + 6 dígitos AAMMDD + 3 de homoclave). Ej: ${EJEMPLO_RFC_MORAL}`;
const MSG_RFC_REP = `RFC del representante (persona física): 4 letras + 6 dígitos de fecha de nacimiento + 3 de homoclave. Ej: ${EJEMPLO_RFC_FISICA}`;
const MSG_CURP = "CURP: 18 caracteres — 4 letras + 6 dígitos (AAMMDD) + H o M + 5 letras + 2 dígitos. Ej: GAGB880430HDFRRN09";
const MSG_CURP_REP = "CURP del representante: 18 caracteres — 4 letras + 6 dígitos (AAMMDD) + H o M + 5 letras + 2 dígitos.";
const MSG_PAIS = "País: código ISO de 2 letras mayúsculas. Ej: MX, US, ES.";
const MSG_CLAVE_PAIS = "Clave de país (lada): código ISO de 2 letras. Ej: MX.";
const MSG_TELEFONO = "Teléfono: 10 a 12 dígitos, solo números. Ej: 9991234567";
const MSG_CORREO = "Correo: formato usuario@dominio.com (se guarda en mayúsculas para el XML).";
const MSG_ACTIVIDAD = "Actividad u ocupación: clave oficial de 7 dígitos del catálogo. Selecciónala de la lista.";
const MSG_GIRO = "Giro mercantil: clave oficial de 7 dígitos del catálogo. Selecciónalo de la lista.";
const MSG_PEP_CARGO = "Indica el cargo o función pública que hace PEP a la persona (p. ej. Presidente municipal, Diputada, Directora de área).";
const MSG_PEP_NOMBRE = "Escribe el nombre de la persona políticamente expuesta con quien existe la relación.";
const MSG_PEP_VINCULO = "Selecciona el tipo de vínculo con la PEP.";
const MSG_PEP_GRADO = "Selecciona el grado de parentesco (1er o 2º grado).";

// ─────────────────────────────────────────────────────────────────────────────
// Relación con PEP (0066) — etiquetas, opciones y textos de ayuda.
// El régimen PEP ya no es un booleano: es una relación (ninguna | es_pep |
// familiar_pep | allegado_pep) que dispara la misma diligencia reforzada. La
// fuente de verdad es `relacion_pep`; el frontend nunca vuelve a escribir el flag
// legado `estatus_pep` (lo deriva el trigger de reconciliación del backend).
// ─────────────────────────────────────────────────────────────────────────────

/** Orden de presentación del selector (ninguna primero, como estado neutro). */
const RELACION_PEP_ORDEN: RelacionPep[] = ["ninguna", "es_pep", "familiar_pep", "allegado_pep"];

/** Etiqueta del botón del selector. */
const RELACION_PEP_ETIQUETA: Record<RelacionPep, string> = {
  ninguna: "Sin relación",
  es_pep: "Es una PEP",
  familiar_pep: "Familiar de una PEP",
  allegado_pep: "Allegado de una PEP",
  // Estado DERIVADO de la empresa (0067): no se ofrece como botón (no está en
  // RELACION_PEP_ORDEN); vive en el mapa por exhaustividad del enum.
  controlada_por_pep: "Controlada por PEP",
};

/** Una línea que explica qué implica la opción elegida (bajo el selector). */
const RELACION_PEP_DESC: Record<RelacionPep, string> = {
  ninguna: "La persona no es PEP ni familiar o allegado de una. No se activa diligencia reforzada por este concepto.",
  es_pep: "La persona desempeña o desempeñó una función pública relevante. Activa diligencia reforzada.",
  familiar_pep: "La persona es cónyuge o pariente (hasta 2º grado) de una PEP. La diligencia reforzada se extiende a familiares.",
  allegado_pep: "La persona es socio, asociado cercano o beneficiario controlador compartido de una PEP. La diligencia reforzada se extiende a allegados.",
  controlada_por_pep: "La empresa es controlada por una o más personas PEP, familiares o allegados de su estructura. La diligencia reforzada se deriva del grafo de control; no se marca a mano.",
};

/** Chip corto para la lista y el detalle (no incluye `ninguna`). */
const RELACION_PEP_CHIP: Record<Exclude<RelacionPep, "ninguna">, string> = {
  es_pep: "PEP",
  familiar_pep: "Familiar de PEP",
  allegado_pep: "Allegado de PEP",
  // Derivado: la empresa cuyo grafo contiene PEP/familiar/allegado (0067).
  controlada_por_pep: "Controlada por PEP",
};

/** Tipos de vínculo de un FAMILIAR de PEP (valor de `pep_vinculo`). */
const VINCULO_FAMILIAR: CatalogoOption[] = [
  ["conyuge", "Cónyuge"],
  ["concubinato", "Concubino(a)"],
  ["consanguinidad", "Parentesco por consanguinidad (de sangre)"],
  ["afinidad", "Parentesco por afinidad (político)"],
];

/** Tipos de vínculo de un ALLEGADO de PEP (valor de `pep_vinculo`). */
const VINCULO_ALLEGADO: CatalogoOption[] = [
  ["socio", "Socio o copropietario"],
  ["asociado_cercano", "Asociado cercano / de negocio"],
  ["beneficiario_comun", "Beneficiario controlador compartido"],
];

/** Grado de parentesco cubierto por el régimen (2º grado vigente, cat_pep_grado_parentesco). */
const PEP_GRADO_SEGUNDO = 2;
const GRADO_OPCIONES: [number, string][] = [
  [1, "1er grado (padres, hijos; suegros, yerno/nuera)"],
  [PEP_GRADO_SEGUNDO, "2º grado (hermanos, abuelos, nietos; cuñados)"],
];

/**
 * Patch que persiste el detalle para el perfil de riesgo/relación PEP. `nivel_riesgo`
 * y los campos PEP viajan por el mismo camino (actualizarPersona). No incluye
 * `estatus_pep`: el backend lo deriva de `relacion_pep` (trigger de reconciliación).
 */
type PatchRiesgoPep = Partial<CamposRelacionPep> & { nivel_riesgo?: string };

/**
 * Resumen PEP del grafo de control de una empresa (0067): cuántas personas
 * físicas de la estructura hay y cuántas están bajo régimen PEP. Lo produce
 * `EstructuraControlBc` (que tiene el grafo) y lo consume el apartado derivado
 * de la empresa para el "N de M personas de la estructura son PEP".
 */
interface ResumenPepGrafo {
  totalPersonasFisicas: number;
  conRegimenPep: number;
}

/** Etiqueta corta por campo para el resumen global de errores (apunta al input). */
const ETIQUETA_CAMPO_ALTA: Record<string, string> = {
  rfc: "RFC",
  curp: "CURP",
  pais: "país de nacionalidad",
  actividad: "actividad u ocupación",
  giro: "giro mercantil",
  con_telefono: "teléfono",
  con_clave_pais: "clave país",
  con_correo: "correo",
  rep_rfc: "RFC del representante",
  rep_curp: "CURP del representante",
  pep_cargo: "cargo o función pública del PEP",
  pep_nombre: "nombre del PEP",
  pep_vinculo: "tipo de vínculo con el PEP",
  pep_grado: "grado de parentesco",
  dom_pais: "país del domicilio",
  dom_estado_provincia: "estado/provincia",
  dom_ciudad: "ciudad/población",
  dom_calle: "calle",
  dom_numero_exterior: "número exterior",
  dom_colonia: "colonia",
  dom_codigo_postal: "código postal",
};

// ─────────────────────────────────────────────────────────────────────────────
// Modo demostración: espejo local del catálogo 0021 (mismo contrato de datos)
// ─────────────────────────────────────────────────────────────────────────────

interface ReqDemo {
  clave: string;
  nombre: string;
  tipos: TipoPersona[];
  obligatorio: boolean;
  ddr: boolean;
  soloPep: boolean;
  // Requisito de beneficiario controlador (0060): además del documento exige el
  // grafo de control capturado. Sólo lo llevan las 3 claves BC.
  grafoBc?: boolean;
}

const REQUISITOS_DEMO: ReqDemo[] = [
  { clave: "pf_identificacion_oficial", nombre: "Identificación oficial (cotejo del original)", tipos: ["pf"], obligatorio: true, ddr: false, soloPep: false },
  { clave: "pf_comprobante_domicilio", nombre: "Comprobante de domicilio", tipos: ["pf"], obligatorio: true, ddr: false, soloPep: false },
  { clave: "pf_constancia_fiscal", nombre: "Constancia de situación fiscal (RFC)", tipos: ["pf"], obligatorio: true, ddr: false, soloPep: false },
  { clave: "pf_declaracion_cuenta_propia", nombre: "Declaración de cuenta propia / dueño beneficiario", tipos: ["pf"], obligatorio: true, ddr: false, soloPep: false },
  { clave: "pm_acta_constitutiva", nombre: "Acta constitutiva", tipos: ["pm"], obligatorio: true, ddr: false, soloPep: false },
  { clave: "pm_poderes_representante", nombre: "Poderes del representante", tipos: ["pm"], obligatorio: true, ddr: false, soloPep: false },
  { clave: "pm_id_representante", nombre: "Identificación del representante (cotejo)", tipos: ["pm"], obligatorio: true, ddr: false, soloPep: false },
  { clave: "pm_constancia_fiscal", nombre: "Constancia de situación fiscal (RFC)", tipos: ["pm"], obligatorio: true, ddr: false, soloPep: false },
  { clave: "pm_declaracion_bc", nombre: "Declaración de Beneficiario Controlador", tipos: ["pm"], obligatorio: true, ddr: false, soloPep: false, grafoBc: true },
  { clave: "pm_cadena_control", nombre: "Cadena de control hasta persona física (>25%)", tipos: ["pm"], obligatorio: true, ddr: false, soloPep: false, grafoBc: true },
  { clave: "fid_contrato", nombre: "Contrato de fideicomiso", tipos: ["fideicomiso"], obligatorio: true, ddr: false, soloPep: false },
  { clave: "fid_constancia_fiscal", nombre: "RFC del fideicomiso", tipos: ["fideicomiso"], obligatorio: true, ddr: false, soloPep: false },
  { clave: "fid_expedientes_roles", nombre: "Expedientes de los roles de control", tipos: ["fideicomiso"], obligatorio: true, ddr: false, soloPep: false, grafoBc: true },
  { clave: "ddr_origen_recursos", nombre: "Acreditación del origen de los recursos", tipos: ["pf", "pm", "fideicomiso"], obligatorio: true, ddr: true, soloPep: false },
  { clave: "ddr_comprobante_ingresos", nombre: "Comprobante de ingresos", tipos: ["pf", "pm", "fideicomiso"], obligatorio: true, ddr: true, soloPep: false },
  { clave: "ddr_declaracion_fiscal", nombre: "Declaración fiscal (anual) reciente", tipos: ["pf", "pm", "fideicomiso"], obligatorio: false, ddr: true, soloPep: false },
  { clave: "ddr_aprobacion_direccion", nombre: "Constancia de aprobación de la alta dirección", tipos: ["pf", "pm", "fideicomiso"], obligatorio: true, ddr: false, soloPep: true },
];

function personaVacia(over: Partial<PersonaRow>): PersonaRow {
  return {
    id: crypto.randomUUID(),
    canonical_id: crypto.randomUUID(),
    tipo: "pf",
    nombre: null,
    apellido_paterno: null,
    apellido_materno: null,
    fecha_nacimiento: null,
    rfc: null,
    curp: null,
    pais_nacionalidad: "MX",
    actividad_economica: null,
    denominacion_razon: null,
    fecha_constitucion: null,
    giro_mercantil: null,
    fiduciario_denominacion: null,
    fideicomiso_identificador: null,
    estatus_pep: false,
    relacion_pep: "ninguna",
    pep_nombre: null,
    pep_cargo: null,
    pep_vinculo: null,
    pep_grado_parentesco: null,
    nivel_riesgo: "pendiente",
    expediente_completo: false,
    domicilio: {},
    contacto: {},
    representante: null,
    created_at: new Date().toISOString(),
    ...over,
  };
}

const PERSONAS_DEMO: PersonaRow[] = [
  personaVacia({ tipo: "pm", denominacion_razon: "Constructora del Sureste, SA de CV", rfc: "CSU160101AB1", giro_mercantil: "2720004" }),
  personaVacia({ tipo: "pf", nombre: "María Fernanda", apellido_paterno: "Uc", apellido_materno: "Cauich", rfc: "UUCM850101AA1", actividad_economica: "3420200" }),
  personaVacia({ tipo: "pf", nombre: "Roberto", apellido_paterno: "Canul", apellido_materno: "Pech", rfc: "CAPR700101BB2", actividad_economica: "3130300", estatus_pep: true, relacion_pep: "es_pep", pep_cargo: "Presidente municipal" }),
  personaVacia({ tipo: "pf", nombre: "Lucía", apellido_paterno: "Canul", apellido_materno: "Herrera", rfc: "CAHL920101CC3", actividad_economica: "3420200", relacion_pep: "familiar_pep", pep_nombre: "Roberto Canul Pech", pep_cargo: "Presidente municipal", pep_vinculo: "consanguinidad", pep_grado_parentesco: 1 }),
];

function estadoDemo(p: PersonaRow, satisfechas: ReadonlySet<string>): RequisitoEstado[] {
  const tipo = p.tipo === "otra" ? "pf" : p.tipo;
  // Espejo del disparo unificado del backend (0066): la DDR (y la aprobación de
  // dirección, `soloPep`) aplican a PEP y también a familiares/allegados, es
  // decir a cualquier relacion_pep <> 'ninguna' — no sólo al booleano legado.
  const enRegimenPep = p.relacion_pep !== "ninguna";
  return REQUISITOS_DEMO.filter(
    (r) =>
      r.tipos.includes(tipo) &&
      (!r.ddr || enRegimenPep || p.nivel_riesgo === "alto") &&
      (!r.soloPep || enRegimenPep),
  ).map((r) => ({
    clave: r.clave,
    nombre: r.nombre,
    descripcion: null,
    requiere_documento: true,
    obligatorio: r.obligatorio,
    es_ddr: r.ddr || r.soloPep,
    satisfecho: satisfechas.has(r.clave),
    // En demo no hay id real de documento; se usa la clave como marcador para
    // que la UI (chip "Documento en bóveda", botón de subir) distinga subido.
    documento_id: satisfechas.has(r.clave) ? r.clave : null,
    requiere_grafo_bc: r.grafoBc ?? false,
    // El modo demostración no captura el grafo BC; se simula que el grafo queda
    // resuelto cuando se sube el documento del requisito, para no dejar el
    // expediente de PM/fideicomiso sin camino a completo. El backend real exige
    // grafo poblado (bc_nodo) además del documento (0060).
    grafo_bc_resuelto: (r.grafoBc ?? false) ? satisfechas.has(r.clave) : null,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de presentación
// ─────────────────────────────────────────────────────────────────────────────

const TIPO_ETIQUETA: Record<string, string> = {
  pf: "Persona física",
  pm: "Persona moral",
  fideicomiso: "Fideicomiso",
  otra: "Otra figura",
};

type CatalogoOption = [string, string];

const ACTIVIDADES_PF_FALLBACK: CatalogoOption[] = [
  ["3420200", "CONSTRUCCION - ARQUITECTOS"],
  ["3130300", "CONSTRUCCION - INGENIEROS, TECNICOS Y OPERADORES DE LA CONSTRUCCION"],
];

const GIROS_PM_FALLBACK: CatalogoOption[] = [
  ["2720004", "CONSTRUCCION - EDIFICACION RESIDENCIAL Y NO RESIDENCIAL"],
];

function opcionesCatalogo(rows: CatalogoRow[]): CatalogoOption[] {
  return rows.map((r) => [r.clave, r.nombre]);
}

function limpio(v: string): string | null {
  const t = v.trim();
  return t === "" ? null : t;
}

function soloDigitos(v: string, max: number): string {
  return v.replace(/[^0-9]/g, "").slice(0, max);
}

function codigoAlfanumerico(v: string, max: number): string {
  return v.toUpperCase().replace(/[^A-ZÑ&%+0-9]/g, "").slice(0, max);
}

function pais2(v: string): string {
  return v.toUpperCase().replace(/[^A-Z]/g, "").slice(0, LONGITUD_PAIS);
}

function contactoValor(contacto: Record<string, unknown>, canonico: string, legacy?: string): string | null {
  const actual = contacto[canonico];
  if (typeof actual === "string" && actual.trim() !== "") return actual;
  const anterior = legacy ? contacto[legacy] : undefined;
  return typeof anterior === "string" && anterior.trim() !== "" ? anterior : null;
}

/** Campos del representante/apoderado que capturan los formularios (prefijo `rep_`). */
const CAMPOS_REPRESENTANTE = [
  "nombre",
  "apellido_paterno",
  "apellido_materno",
  "fecha_nacimiento",
  "rfc",
  "curp",
] as const;

/**
 * Arma el JSONB `representante` a partir de los campos `rep_*` del formulario.
 * Es el mismo objeto que consumen los serializadores (INM/DIN/ARI): PM lo mapea a
 * `representante_apoderado`, fideicomiso a `apoderado_delegado`. Devuelve null si
 * no se capturó nada (no forzamos objeto vacío). Los serializadores exigen nombre
 * + ambos apellidos; aquí no bloqueamos para permitir guardar y subsanar después.
 */
function armarRepresentante(campo: (k: string) => string): Record<string, string> | null {
  const rep: Record<string, string> = {};
  for (const k of CAMPOS_REPRESENTANTE) {
    const v = limpio(campo(`rep_${k}`));
    if (v) rep[k] = v;
  }
  return Object.keys(rep).length > 0 ? rep : null;
}

/**
 * Errores del representante/apoderado (siempre persona física), indexados por
 * campo (`rep_rfc`, `rep_curp`). Se comparte entre el alta (PM/fideicomiso) y el
 * editor de subsanación del detalle para dar el mismo mensaje accionable.
 */
function erroresRepresentante(campo: (k: string) => string): Record<string, string> {
  const errores: Record<string, string> = {};
  const rfc = campo("rep_rfc").trim();
  const curp = campo("rep_curp").trim();
  if (rfc && !RFC_FISICA_RE.test(rfc)) errores.rep_rfc = MSG_RFC_REP;
  if (curp && !CURP_RE.test(curp)) errores.rep_curp = MSG_CURP_REP;
  return errores;
}

/**
 * Valida el alta y devuelve los errores INDEXADOS POR CAMPO (clave del formulario
 * → mensaje específico con ejemplo), para pintarlos pegados al input que falla.
 * La regla de estructura no cambia (mismos regex); lo que cambia es el mensaje.
 */
function erroresNuevaPersona(
  tipo: TipoPersona,
  campo: (k: string) => string,
  claveEconomicaValida: boolean,
): Record<string, string> {
  const errores: Record<string, string> = {};
  const rfc = campo("rfc").trim();
  const curp = campo("curp").trim();
  const pais = (limpio(campo("pais")) ?? "MX").trim();
  const telefono = campo("con_telefono").trim();
  const correo = campo("con_correo").trim();
  const clavePais = (limpio(campo("con_clave_pais")) ?? "MX").trim();

  if (rfc) {
    const rfcValido = tipo === "pf"
      ? RFC_FISICA_RE.test(rfc)
      : tipo === "pm"
        ? RFC_MORAL_RE.test(rfc)
        : RFC_MORAL_RE.test(rfc) || RFC_FISICA_RE.test(rfc);
    if (!rfcValido) errores.rfc = tipo === "pf" ? MSG_RFC_FISICA : tipo === "pm" ? MSG_RFC_MORAL : MSG_RFC_FIDEICOMISO;
  }
  if (curp && !CURP_RE.test(curp)) errores.curp = MSG_CURP;
  if ((tipo === "pf" || tipo === "pm") && !PAIS_RE.test(pais)) errores.pais = MSG_PAIS;
  if (telefono && !TELEFONO_RE.test(telefono)) errores.con_telefono = MSG_TELEFONO;
  if ((telefono || correo) && !PAIS_RE.test(clavePais)) errores.con_clave_pais = MSG_CLAVE_PAIS;
  if (correo && !CORREO_RE.test(correo)) errores.con_correo = MSG_CORREO;
  if (!claveEconomicaValida) errores[tipo === "pf" ? "actividad" : "giro"] = tipo === "pf" ? MSG_ACTIVIDAD : MSG_GIRO;
  if (tipo === "pm" || tipo === "fideicomiso") Object.assign(errores, erroresRepresentante(campo));
  Object.assign(errores, erroresRelacionPep(campo));
  Object.assign(errores, erroresDomicilioPersona(campo));

  return errores;
}

/**
 * Errores de la captura de la relación con PEP, indexados por campo del
 * formulario (`pep_cargo`, `pep_nombre`, `pep_vinculo`, `pep_grado`). Sólo aplican
 * cuando la relación no es `ninguna`. Compartidos por el alta y el editor del
 * detalle para dar el mismo mensaje accionable. Regla:
 *  - siempre: cargo/función pública que sustenta la calidad de PEP;
 *  - familiar/allegado: además el nombre del PEP y el tipo de vínculo;
 *  - familiar por parentesco (consanguinidad/afinidad): además el grado.
 */
function erroresRelacionPep(campo: (k: string) => string): Record<string, string> {
  const errores: Record<string, string> = {};
  const rel = (campo("pep_relacion") || "ninguna") as RelacionPep;
  if (rel === "ninguna") return errores;
  const cargo = campo("pep_cargo").trim();
  const nombre = campo("pep_nombre").trim();
  const vinculo = campo("pep_vinculo").trim();
  const grado = campo("pep_grado").trim();
  if (!cargo) errores.pep_cargo = MSG_PEP_CARGO;
  if (rel === "familiar_pep" || rel === "allegado_pep") {
    if (!nombre) errores.pep_nombre = MSG_PEP_NOMBRE;
    if (!vinculo) errores.pep_vinculo = MSG_PEP_VINCULO;
  }
  if (rel === "familiar_pep" && (vinculo === "consanguinidad" || vinculo === "afinidad") && !grado) {
    errores.pep_grado = MSG_PEP_GRADO;
  }
  return errores;
}

// ─────────────────────────────────────────────────────────────────────────────
// Campos reutilizables — DEFINIDOS A NIVEL DE MÓDULO (no dentro del render).
// Definirlos dentro de un componente les da una identidad nueva en cada render
// (cada tecleo → setF → re-render), y React desmonta/remonta el <input>, con lo
// que se pierde el foco a cada letra (issue #45). Como componentes estables aquí,
// el input conserva su identidad y el foco. El estado viaja por props (valor +
// onChange), no por closure.
// ─────────────────────────────────────────────────────────────────────────────

function CampoTexto({
  etiqueta,
  valor,
  onCambio,
  tipo = "text",
  ancho,
  inputMode,
  maxLength,
  normaliza,
  error,
}: {
  etiqueta: string;
  valor: string;
  onCambio: (v: string) => void;
  tipo?: string;
  ancho?: "medio";
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
  /** Transforma el valor tecleado antes de guardarlo (mayúsculas, sólo dígitos…). */
  normaliza?: (v: string) => string;
  /** Mensaje de validación específico; si viene, se pinta en rojo bajo el input. */
  error?: string | undefined;
}) {
  return (
    <div style={{ gridColumn: ancho === "medio" ? "span 1" : "span 2" }}>
      <label style={st.label}>{etiqueta}</label>
      <input
        style={error ? { ...st.input, borderColor: C.red } : st.input}
        type={tipo}
        inputMode={inputMode}
        maxLength={maxLength}
        value={valor}
        onChange={(e) => { onCambio(normaliza ? normaliza(e.target.value) : e.target.value); }}
      />
      {error && <p style={st.errorCampo}>{error}</p>}
    </div>
  );
}

function CampoCatalogo({
  etiqueta,
  valor,
  onCambio,
  opciones,
  error,
}: {
  etiqueta: string;
  valor: string;
  onCambio: (v: string) => void;
  opciones: CatalogoOption[];
  error?: string | undefined;
}) {
  const estilo = error ? { ...st.input, borderColor: C.red } : st.input;
  return (
    <div style={{ gridColumn: "span 1" }}>
      <label style={st.label}>{etiqueta}</label>
      {opciones.length > 0 ? (
        <select style={estilo} value={valor} onChange={(e) => { onCambio(e.target.value); }}>
          <option value="">Selecciona…</option>
          {opciones.map(([clave, nombre]) => (
            <option key={clave} value={clave}>{clave} · {nombre}</option>
          ))}
        </select>
      ) : (
        <input
          style={estilo}
          inputMode="numeric"
          maxLength={LONGITUD_ACTIVIDAD_GIRO}
          value={valor}
          onChange={(e) => { onCambio(soloDigitos(e.target.value, LONGITUD_ACTIVIDAD_GIRO)); }}
        />
      )}
      {error && <p style={st.errorCampo}>{error}</p>}
    </div>
  );
}

/**
 * Tarjeta de representante/apoderado (PM) o apoderado/delegado (fideicomiso).
 * Los 3 XSD (INM, DIN, ARI) exigen nombre + apellido paterno + apellido materno;
 * la fecha de nacimiento la piden algunos avisos. Los prefijos de campo (`rep_`)
 * los comparte con `armarDatos()`, que lee de aquí para armar el JSONB
 * `representante` (issue #32).
 */
function TarjetaRepresentante({
  titulo,
  campo,
  setCampo,
  errores,
}: {
  titulo: string;
  campo: (k: string) => string;
  setCampo: (k: string, v: string) => void;
  /** Errores por campo del representante (rep_rfc, rep_curp) para pintar inline. */
  errores?: Record<string, string> | undefined;
}) {
  return (
    <div style={st.tarjeta}>
      <p style={st.eyebrow}>{titulo}</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <CampoTexto etiqueta="Nombre(s)" ancho="medio" valor={campo("rep_nombre")} onCambio={(v) => { setCampo("rep_nombre", v); }} />
        <CampoTexto etiqueta="Apellido paterno" ancho="medio" valor={campo("rep_apellido_paterno")} onCambio={(v) => { setCampo("rep_apellido_paterno", v); }} />
        <CampoTexto etiqueta="Apellido materno" ancho="medio" valor={campo("rep_apellido_materno")} onCambio={(v) => { setCampo("rep_apellido_materno", v); }} />
        <CampoTexto etiqueta="Fecha de nacimiento" tipo="date" ancho="medio" valor={campo("rep_fecha_nacimiento")} onCambio={(v) => { setCampo("rep_fecha_nacimiento", v); }} />
        <CampoTexto etiqueta="RFC" ancho="medio" maxLength={13} normaliza={(v) => v.toUpperCase()} valor={campo("rep_rfc")} onCambio={(v) => { setCampo("rep_rfc", v); }} error={errores?.rep_rfc} />
        <CampoTexto etiqueta="CURP" ancho="medio" maxLength={18} normaliza={(v) => v.toUpperCase()} valor={campo("rep_curp")} onCambio={(v) => { setCampo("rep_curp", v); }} error={errores?.rep_curp} />
      </div>
      <p style={{ margin: "8px 0 0", fontSize: 12, color: C.muted }}>
        Nombre y ambos apellidos son obligatorios para el aviso (INM, DIN y ARI). Sin
        ellos el XML no se genera.
      </p>
    </div>
  );
}

/**
 * Editor del representante/apoderado en el DETALLE de una persona existente
 * (subsanación, issue #32). Mantiene su propio borrador `rep_*` sembrado desde el
 * JSONB guardado, reusa TarjetaRepresentante y persiste con el mismo armador que
 * el alta. Sirve para completar apellido materno / fecha de nacimiento que faltaban
 * y que hacían tronar el XML.
 */
function EditorRepresentante({
  titulo,
  representante,
  onGuardar,
}: {
  titulo: string;
  representante: Record<string, unknown> | null;
  onGuardar: (representante: Record<string, string> | null) => void;
}) {
  const inicial = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    for (const k of CAMPOS_REPRESENTANTE) {
      const v = representante?.[k];
      if (typeof v === "string") f[`rep_${k}`] = v;
    }
    return f;
  }, [representante]);

  const [f, setF] = useState<Record<string, string>>(inicial);
  const [guardando, setGuardando] = useState(false);
  const campo = (k: string) => f[k] ?? "";
  const setCampo = (k: string, v: string) => { setF((prev) => ({ ...prev, [k]: v })); };

  const rep = armarRepresentante(campo);
  const completo = rep !== null && !!rep["nombre"] && !!rep["apellido_paterno"] && !!rep["apellido_materno"];
  const sucio = JSON.stringify(f) !== JSON.stringify(inicial);
  const erroresRep = erroresRepresentante(campo);
  const hayErroresRep = Object.keys(erroresRep).length > 0;

  return (
    <div>
      <TarjetaRepresentante titulo={titulo} campo={campo} setCampo={setCampo} errores={erroresRep} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "-6px 0 14px" }}>
        <button
          style={{ ...st.btnPrim, opacity: guardando || !sucio || hayErroresRep ? OPACIDAD_GUARDANDO : 1 }}
          disabled={guardando || !sucio || hayErroresRep}
          onClick={() => {
            setGuardando(true);
            try {
              onGuardar(rep);
            } finally {
              setGuardando(false);
            }
          }}
        >
          {guardando ? "Guardando…" : "Guardar representante"}
        </button>
        {!completo && (
          <span style={{ fontSize: 12, color: C.amber }}>
            Nombre y ambos apellidos son obligatorios para generar el aviso.
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Relación con PEP — apartado propio (alta y detalle). Componente presentacional
// a nivel de módulo (no dentro del render — issue #45) para no perder el foco.
// El estado viaja por props (campo/setCampo), igual que TarjetaRepresentante, de
// modo que el alta lo controla desde su borrador `f` y el detalle desde el suyo.
// ─────────────────────────────────────────────────────────────────────────────

function SelectorRelacionPep({
  campo,
  setCampo,
  errores,
  children,
}: {
  campo: (k: string) => string;
  setCampo: (k: string, v: string) => void;
  /** Errores por campo de la relación PEP (pep_cargo, pep_nombre, pep_vinculo, pep_grado). */
  errores?: Record<string, string> | undefined;
  /** Pie de la tarjeta: aviso de reversión + botón de guardar (sólo en el detalle). */
  children?: ReactNode;
}) {
  const rel = (campo("pep_relacion") || "ninguna") as RelacionPep;
  const vinculo = campo("pep_vinculo");
  const opcionesVinculo = rel === "familiar_pep" ? VINCULO_FAMILIAR : rel === "allegado_pep" ? VINCULO_ALLEGADO : [];
  const muestraGrado = rel === "familiar_pep" && (vinculo === "consanguinidad" || vinculo === "afinidad");

  const elegir = (nueva: RelacionPep) => {
    setCampo("pep_relacion", nueva);
    // Al cambiar de relación, el vínculo/grado dependientes dejan de aplicar: se
    // limpian para no arrastrar un valor inválido a la nueva relación.
    setCampo("pep_vinculo", "");
    setCampo("pep_grado", "");
    // Volver a "sin relación" limpia también el nombre/cargo del PEP, para que la
    // captura quede vacía como quedará en el backend (invariante ck_relacion_pep_datos)
    // y el estado "sin cambios" se resuelva tras guardar la corrección.
    if (nueva === "ninguna") {
      setCampo("pep_nombre", "");
      setCampo("pep_cargo", "");
    }
  };

  return (
    <div style={st.tarjeta}>
      <p style={st.eyebrow}>Relación con persona políticamente expuesta (PEP)</p>
      <p style={{ margin: "0 0 10px", fontSize: 12.5, color: C.muted, lineHeight: 1.45 }}>
        La diligencia reforzada no aplica sólo si la persona <strong>es</strong> una PEP: también
        si es <strong>familiar</strong> (hasta 2º grado) o <strong>allegado</strong> de una. En
        esos casos exige aprobación de la alta dirección, acreditar el origen de los recursos y
        monitoreo continuo (Reglamento LFPIORPI, Cap. Sexto Bis; GAFI Rec. 12).
      </p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {RELACION_PEP_ORDEN.map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={rel === r}
            onClick={() => { elegir(r); }}
            style={{
              ...st.btn,
              borderColor: rel === r ? C.tiel : C.line,
              background: rel === r ? C.tielSoft : "#fff",
              color: rel === r ? C.tielDark : C.ink,
            }}
          >
            {RELACION_PEP_ETIQUETA[r]}
          </button>
        ))}
      </div>
      <p style={{ margin: "8px 0 0", fontSize: 12, color: rel === "ninguna" ? C.muted : C.ink, lineHeight: 1.4 }}>
        {RELACION_PEP_DESC[rel]}
      </p>

      {rel !== "ninguna" && (
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {rel !== "es_pep" && (
            <CampoTexto
              etiqueta="Nombre del PEP"
              valor={campo("pep_nombre")}
              onCambio={(v) => { setCampo("pep_nombre", v); }}
              error={errores?.pep_nombre}
            />
          )}
          <CampoTexto
            etiqueta={rel === "es_pep" ? "Cargo o función pública" : "Cargo o función pública del PEP"}
            valor={campo("pep_cargo")}
            onCambio={(v) => { setCampo("pep_cargo", v); }}
            error={errores?.pep_cargo}
          />
          {(rel === "familiar_pep" || rel === "allegado_pep") && (
            <div style={{ gridColumn: "span 1" }}>
              <label style={st.label}>Tipo de vínculo</label>
              <select
                style={errores?.pep_vinculo ? { ...st.input, borderColor: C.red } : st.input}
                value={vinculo}
                onChange={(e) => {
                  const v = e.target.value;
                  setCampo("pep_vinculo", v);
                  // El grado sólo aplica a parentesco; si el vínculo deja de serlo, se limpia.
                  if (v !== "consanguinidad" && v !== "afinidad") setCampo("pep_grado", "");
                }}
              >
                <option value="">Selecciona…</option>
                {opcionesVinculo.map(([clave, nombre]) => (
                  <option key={clave} value={clave}>{nombre}</option>
                ))}
              </select>
              {errores?.pep_vinculo && <p style={st.errorCampo}>{errores.pep_vinculo}</p>}
            </div>
          )}
          {muestraGrado && (
            <div style={{ gridColumn: "span 1" }}>
              <label style={st.label}>Grado de parentesco</label>
              <select
                style={errores?.pep_grado ? { ...st.input, borderColor: C.red } : st.input}
                value={campo("pep_grado")}
                onChange={(e) => { setCampo("pep_grado", e.target.value); }}
              >
                <option value="">Selecciona…</option>
                {GRADO_OPCIONES.map(([valor, nombre]) => (
                  <option key={valor} value={String(valor)}>{nombre}</option>
                ))}
              </select>
              {errores?.pep_grado && <p style={st.errorCampo}>{errores.pep_grado}</p>}
            </div>
          )}
        </div>
      )}

      {children}
    </div>
  );
}

/**
 * Editor de la relación con PEP en el DETALLE de una persona existente. Mantiene
 * un borrador `pep_*` sembrado desde la fila guardada, reusa SelectorRelacionPep y
 * persiste con un botón explícito (no por tecleo). El caso que más importa: cuando
 * el borrador vuelve a "sin relación" en alguien que hoy ESTÁ bajo régimen PEP,
 * avisa claramente que guardar es una CORRECCIÓN — revierte la diligencia reforzada
 * y el backend cierra por corrección el caso de aprobación PEP (no se queda pegado).
 */
export function EditorRelacionPep({
  persona,
  onGuardar,
}: {
  persona: PersonaRow;
  onGuardar: (campos: CamposRelacionPep) => void;
}) {
  const inicial = useMemo<Record<string, string>>(() => ({
    pep_relacion: persona.relacion_pep,
    pep_nombre: persona.pep_nombre ?? "",
    pep_cargo: persona.pep_cargo ?? "",
    pep_vinculo: persona.pep_vinculo ?? "",
    pep_grado: persona.pep_grado_parentesco != null ? String(persona.pep_grado_parentesco) : "",
  }), [persona]);

  const [f, setF] = useState<Record<string, string>>(inicial);
  const [guardando, setGuardando] = useState(false);
  const campo = (k: string) => f[k] ?? "";
  const setCampo = (k: string, v: string) => { setF((prev) => ({ ...prev, [k]: v })); };

  const errores = erroresRelacionPep(campo);
  const hayErrores = Object.keys(errores).length > 0;
  const sucio = JSON.stringify(f) !== JSON.stringify(inicial);
  const relDraft = (campo("pep_relacion") || "ninguna") as RelacionPep;
  const revierte = relDraft === "ninguna" && persona.relacion_pep !== "ninguna";

  const guardar = () => {
    setGuardando(true);
    try {
      onGuardar(normalizarRelacionPep({
        relacion: relDraft,
        nombre: campo("pep_nombre"),
        cargo: campo("pep_cargo"),
        vinculo: campo("pep_vinculo"),
        grado: campo("pep_grado"),
      }));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <SelectorRelacionPep campo={campo} setCampo={setCampo} errores={errores}>
      {revierte && (
        <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 12, background: C.amberBg, color: C.amber, fontSize: 12.5, lineHeight: 1.45 }}>
          <strong>Vas a quitar la relación con PEP.</strong> Al guardar, esto se trata como una
          corrección: se revierte la diligencia reforzada y el caso de aprobación de dirección que
          estaba abierto se cierra por corrección — no se queda pendiente. Queda el rastro en la
          bitácora de que estuvo marcado.
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          style={{ ...st.btnPrim, opacity: guardando || !sucio || hayErrores ? OPACIDAD_GUARDANDO : 1 }}
          disabled={guardando || !sucio || hayErrores}
          onClick={guardar}
        >
          {guardando ? "Guardando…" : revierte ? "Guardar corrección (quitar PEP)" : "Guardar relación con PEP"}
        </button>
        {!sucio && persona.relacion_pep !== "ninguna" && (
          <span style={{ fontSize: 12, color: C.muted }}>Diligencia reforzada activa por esta relación.</span>
        )}
      </div>
    </SelectorRelacionPep>
  );
}

/**
 * Editor COMPACTO de la relación con PEP de UNA persona física del grafo de
 * control (migración 0067). Reusa `SelectorRelacionPep` (0066) sobre un borrador
 * sembrado desde el nodo; al aplicar, normaliza y entrega los campos al padre,
 * que los funde en el nodo. No persiste solo: el grafo se guarda completo con
 * `bc_guardar_grafo`. Solo se monta para nodos `tipo='pf'` (el CHECK del backend
 * prohíbe datos PEP en nodos PM/fideicomiso).
 *
 * Rol tras integrar la relación con PEP en el ALTA (form "Agregar persona a la
 * cadena"): este editor pasa a ser la vía de CORRECCIÓN/EDICIÓN de un nodo ya
 * agregado — el único camino para cambiar la relación después del alta (el alta
 * sólo captura al nacer). No es redundante con el alta: no coexisten para el mismo
 * nodo (el alta captura al crear; la fila corrige después). Por eso se conserva.
 */
function NodoPepEditor({
  nodo,
  onAplicar,
  onCancelar,
}: {
  nodo: BcNodo;
  onAplicar: (campos: CamposRelacionPep) => void;
  onCancelar: () => void;
}) {
  const inicial = useMemo<Record<string, string>>(() => ({
    pep_relacion: nodo.relacion_pep ?? "ninguna",
    pep_nombre: nodo.pep_nombre ?? "",
    pep_cargo: nodo.pep_cargo ?? "",
    pep_vinculo: nodo.pep_vinculo ?? "",
    pep_grado: nodo.pep_grado_parentesco != null ? String(nodo.pep_grado_parentesco) : "",
  }), [nodo]);

  const [f, setF] = useState<Record<string, string>>(inicial);
  const campo = (k: string) => f[k] ?? "";
  const setCampo = (k: string, v: string) => { setF((prev) => ({ ...prev, [k]: v })); };

  const errores = erroresRelacionPep(campo);
  const hayErrores = Object.keys(errores).length > 0;
  const relDraft = (campo("pep_relacion") || "ninguna") as RelacionPep;

  const aplicar = () => {
    onAplicar(normalizarRelacionPep({
      relacion: relDraft,
      nombre: campo("pep_nombre"),
      cargo: campo("pep_cargo"),
      vinculo: campo("pep_vinculo"),
      grado: campo("pep_grado"),
    }));
  };

  return (
    <SelectorRelacionPep campo={campo} setCampo={setCampo} errores={errores}>
      <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          style={{ ...st.btnPrim, opacity: hayErrores ? OPACIDAD_GUARDANDO : 1 }}
          disabled={hayErrores}
          onClick={aplicar}
        >
          Aplicar relación con PEP
        </button>
        <button type="button" style={st.btn} onClick={onCancelar}>Cancelar</button>
      </div>
    </SelectorRelacionPep>
  );
}

/**
 * Apartado "Relación con PEP" DERIVADO para empresa (PM/fideicomiso), solo
 * lectura (migración 0067). La empresa no se marca PEP a mano: su estatus lo
 * materializa el backend a `relacion_pep = 'controlada_por_pep'` cuando su grafo
 * de control contiene una persona PEP/familiar/allegado, o `'ninguna'` si no.
 * Aquí se MUESTRA el resultado y se remite a capturarlo en Estructura de control.
 */
export function ApartadoPepEmpresa({
  persona,
  resumen,
}: {
  persona: PersonaRow;
  resumen: ResumenPepGrafo | null;
}) {
  const activa = persona.relacion_pep === "controlada_por_pep";
  return (
    <div style={st.tarjeta}>
      <p style={st.eyebrow}>Relación con persona políticamente expuesta (PEP)</p>
      {activa ? (
        <div style={{ padding: "10px 14px", borderRadius: 12, background: C.redBg, color: C.red, fontSize: 12.5, lineHeight: 1.45 }}>
          <strong>Diligencia reforzada activa por PEP en la estructura.</strong>{" "}
          {resumen !== null && resumen.conRegimenPep > 0
            ? `${String(resumen.conRegimenPep)} de ${String(resumen.totalPersonasFisicas)} personas físicas de la estructura de control son PEP, familiar o allegado.`
            : "Al menos una persona de la estructura de control es PEP, familiar o allegado."}
          {" "}Se activan aprobación de la alta dirección, acreditación del origen de los recursos y
          monitoreo continuo.
        </div>
      ) : (
        <div style={{ padding: "10px 14px", borderRadius: 12, background: C.grayBg, color: C.muted, fontSize: 12.5, lineHeight: 1.45 }}>
          <strong style={{ color: C.ink }}>Sin PEP en la estructura de control.</strong>{" "}
          Ninguna persona de la estructura capturada es PEP, familiar o allegado de una.
        </div>
      )}
      <p style={{ margin: "10px 0 0", fontSize: 12, color: C.muted, lineHeight: 1.45 }}>
        La empresa no se marca PEP a mano: este estatus se <strong>deriva</strong> del grafo de
        control. Captura o corrige la relación con PEP de cada persona en la sección
        <strong> Estructura de control</strong>, más abajo.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Estructura de control (beneficiario controlador) — captura del grafo BC.
// Visible SOLO para PM y fideicomiso. Componente a nivel de módulo (no dentro del
// render — issue #45) para no perder el foco de los inputs en cada tecleo.
// ─────────────────────────────────────────────────────────────────────────────

const PORCENTAJE_MAX = 100;

const TIPO_CONTROL_ETIQUETA: Record<TipoControlBc, string> = {
  participacion: "Participación (capital)",
  derechos_voto: "Derechos de voto",
  otros_medios: "Otros medios (control efectivo)",
};

const TIPOS_CONTROL: TipoControlBc[] = ["participacion", "derechos_voto", "otros_medios"];

/** Nodo raíz sembrado desde la persona editada (el cliente PM/fideicomiso analizado). */
function nodoRaizDesdePersona(p: PersonaRow): BcNodo {
  return {
    id: crypto.randomUUID(),
    tipo: p.tipo,
    es_raiz: true,
    nombre: p.nombre,
    denominacion: p.denominacion_razon ?? p.fiduciario_denominacion,
    rfc: p.rfc,
    curp: p.curp,
    pais_nacionalidad: p.pais_nacionalidad,
    apellido_paterno: p.apellido_paterno,
    apellido_materno: p.apellido_materno,
    fecha_nacimiento: p.fecha_nacimiento,
  };
}

/** Nombre legible de un nodo (PF: nombre + apellidos; PM/fideicomiso: denominación). */
function nombreNodo(n: BcNodo): string {
  if (n.tipo === "pf") {
    const completo = [n.nombre, n.apellido_paterno, n.apellido_materno]
      .filter((s): s is string => typeof s === "string" && s.trim() !== "")
      .join(" ");
    return completo === "" ? "(sin nombre)" : completo;
  }
  const den = n.denominacion ?? n.nombre;
  return den === null || den.trim() === "" ? "(sin denominación)" : den;
}

/** Frase de una arista para la lista ("X posee 50% de Y" / "X controla a Y por otros medios"). */
function textoArista(a: BcArista, nodoPorId: ReadonlyMap<string, BcNodo>): string {
  const origen = nodoPorId.get(a.origen_nodo_id);
  const destino = nodoPorId.get(a.destino_nodo_id);
  const o = origen ? nombreNodo(origen) : "?";
  const d = destino ? nombreNodo(destino) : "?";
  if (a.tipo_control === "otros_medios") return `${o} controla a ${d} por otros medios`;
  const via = a.tipo_control === "derechos_voto" ? " de derechos de voto" : "";
  return `${o} posee ${String(a.participacion)}%${via} de ${d}`;
}

/**
 * Encabezado de paso numerado para guiar la captura del grafo BC. El número va
 * como texto (no como dato) y la instrucción breve va como children.
 */
function PasoBc({ numero, titulo, children }: { numero: string; titulo: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, margin: "18px 0 10px" }}>
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: 999,
          background: C.tiel,
          color: "#fff",
          fontSize: 13,
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {numero}
      </span>
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.ink }}>{titulo}</p>
        <p style={{ margin: "2px 0 0", fontSize: 12.5, color: C.muted, lineHeight: 1.4 }}>{children}</p>
      </div>
    </div>
  );
}

/**
 * Editor de la cadena de control de un cliente PM/fideicomiso: da de alta nodos
 * (PF/PM) y aristas (quién controla a quién, %, vía de control), pobla el grafo
 * BC (bc_nodo/bc_arista) vía bc_guardar_grafo y muestra los beneficiarios
 * controladores que el backend recalcula (PF >25% o control por otros medios).
 * Se auto-oculta (null) para PF; el detalle sólo lo monta para PM/fideicomiso.
 */
export function EstructuraControlBc({
  persona,
  token,
  demo,
  onGuardado,
  onResumenPep,
  onEmpresaRelacionPep,
}: {
  persona: PersonaRow;
  token: string;
  demo: boolean;
  /** Se invoca tras guardar bien para refrescar el expediente (chips) del padre. */
  onGuardado?: (() => Promise<void>) | undefined;
  /** Reporta al padre el resumen PEP del grafo (para el apartado derivado de la empresa). */
  onResumenPep?: ((resumen: ResumenPepGrafo) => void) | undefined;
  /**
   * Reporta la relación PEP DERIVADA de la empresa al guardar el grafo
   * (`controlada_por_pep` si algún nodo PF es PEP, si no `ninguna`). El padre la
   * refleja de inmediato en el apartado derivado; en backend real el trigger de
   * 0067 la materializa y el refresco la confirma.
   */
  onEmpresaRelacionPep?: ((rel: RelacionPep) => void) | undefined;
}) {
  const esControlable = persona.tipo === "pm" || persona.tipo === "fideicomiso";

  // El grafo se ata a canonical_id, no a la identidad del objeto `persona`: así un
  // refresco del padre (recargar personas/expediente tras guardar) no vuelve a
  // cargar el grafo ni borra el resultado recién mostrado. Se lee vía ref.
  const personaRef = useRef(persona);
  personaRef.current = persona;

  const [nodos, setNodos] = useState<BcNodo[]>([]);
  const [aristas, setAristas] = useState<BcArista[]>([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [beneficiarios, setBeneficiarios] = useState<BcBeneficiario[] | null>(null);
  // Nº de beneficiarios de la última corrida guardada (null = no se ha guardado en
  // esta edición). Dispara el mensaje de éxito. Se invalida al editar la cadena.
  const [guardado, setGuardado] = useState<number | null>(null);

  const [nodoTipo, setNodoTipo] = useState<"pf" | "pm">("pf");
  const [nodoCampos, setNodoCampos] = useState<Record<string, string>>({});
  const [aOrigen, setAOrigen] = useState("");
  const [aDestino, setADestino] = useState("");
  const [aTipo, setATipo] = useState<TipoControlBc>("participacion");
  const [aPct, setAPct] = useState("");
  // Nodo cuyo editor de relación con PEP está expandido (uno a la vez). null = ninguno.
  const [pepAbiertoId, setPepAbiertoId] = useState<string | null>(null);

  // Resumen PEP del grafo (personas físicas y cuántas bajo régimen PEP). Fuente de
  // verdad por-nodo = relacion_pep hidratado; se reporta al padre para el apartado
  // derivado de la empresa. Sólo cuenta nodos PF (un PM no es PEP).
  const resumenPep = useMemo<ResumenPepGrafo>(() => {
    const pf = nodos.filter((n) => n.tipo === "pf");
    const conPep = pf.filter((n) => (n.relacion_pep ?? "ninguna") !== "ninguna").length;
    return { totalPersonasFisicas: pf.length, conRegimenPep: conPep };
  }, [nodos]);

  useEffect(() => { onResumenPep?.(resumenPep); }, [resumenPep, onResumenPep]);

  const nodoPorId = useMemo(
    () => new Map<string, BcNodo>(nodos.map((n): [string, BcNodo] => [n.id, n])),
    [nodos],
  );

  const cargarGrafo = useCallback(async () => {
    const p = personaRef.current;
    if (demo) {
      setNodos([nodoRaizDesdePersona(p)]);
      setAristas([]);
      setBeneficiarios(null);
      setGuardado(null);
      setCargando(false);
      return;
    }
    setCargando(true);
    setBeneficiarios(null);
    setGuardado(null);
    const g = await obtenerGrafoBc(token, p.canonical_id);
    setNodos(g.nodos.length > 0 ? g.nodos : [nodoRaizDesdePersona(p)]);
    setAristas(g.aristas);
    setCargando(false);
  }, [demo, token]);

  useEffect(() => {
    cargarGrafo().catch((e: unknown) => {
      setError(e instanceof Error ? e.message : "No se pudo cargar la estructura de control.");
      setCargando(false);
    });
  }, [cargarGrafo]);

  const campoNodo = (k: string) => nodoCampos[k] ?? "";
  const setCampoNodo = (k: string, v: string) => { setNodoCampos((prev) => ({ ...prev, [k]: v })); };

  // El resultado (beneficiarios + mensaje de éxito) queda obsoleto en cuanto se
  // toca la cadena: se limpia para que nadie lea un cálculo viejo sin re-guardar.
  const invalidarResultado = () => {
    setBeneficiarios(null);
    setGuardado(null);
  };

  const nodoRfc = campoNodo("rfc").trim();
  const nodoRfcValido = nodoRfc === "" || (nodoTipo === "pf" ? RFC_FISICA_RE.test(nodoRfc) : RFC_MORAL_RE.test(nodoRfc));
  const errorNodoRfc = nodoRfc !== "" && !nodoRfcValido
    ? (nodoTipo === "pf" ? MSG_RFC_FISICA : MSG_RFC_MORAL)
    : undefined;
  // La relación con PEP se captura EN EL PROPIO ALTA, pero sólo para persona física:
  // un nodo PM no puede ser PEP (el CHECK del backend 0067 lo prohíbe). Mismos
  // errores por campo que el editor post-alta; bloquean "Agregar persona" hasta
  // resolverse (p. ej. "Es una PEP" sin cargo).
  const erroresPepNodo: Record<string, string> = nodoTipo === "pf" ? erroresRelacionPep(campoNodo) : {};
  const hayErroresPepNodo = Object.keys(erroresPepNodo).length > 0;
  const nodoValido = (nodoTipo === "pf"
    ? campoNodo("nombre").trim() !== ""
    : campoNodo("denominacion").trim() !== "") && nodoRfcValido && !hayErroresPepNodo;

  const aPctNum = Number(aPct);
  const aPctValido = aTipo === "otros_medios"
    || (aPct.trim() !== "" && Number.isFinite(aPctNum) && aPctNum > 0 && aPctNum <= PORCENTAJE_MAX);
  const errorPct = aTipo !== "otros_medios" && aPct.trim() !== "" && !aPctValido
    ? "Indica un porcentaje entre 1 y 100."
    : undefined;
  const aristaValida = aOrigen !== "" && aDestino !== "" && aOrigen !== aDestino && aPctValido;

  const agregarNodo = () => {
    const comun = {
      id: crypto.randomUUID(),
      es_raiz: false,
      rfc: limpio(campoNodo("rfc")),
      pais_nacionalidad: "MX",
      curp: null,
    };
    const nodo: BcNodo = nodoTipo === "pf"
      ? {
          ...comun,
          tipo: "pf",
          nombre: limpio(campoNodo("nombre")),
          apellido_paterno: limpio(campoNodo("apellido_paterno")),
          apellido_materno: limpio(campoNodo("apellido_materno")),
          denominacion: null,
          fecha_nacimiento: null,
          // El nodo NACE con su relación con PEP ya capturada: funde los pep_*
          // exactamente igual que el editor post-alta (aplicarPepNodo), imponiendo
          // las mismas invariantes con normalizarRelacionPep. Persona moral no lo
          // lleva (el CHECK del backend 0067 lo prohíbe), por eso sólo va en la rama pf.
          ...normalizarRelacionPep({
            relacion: (campoNodo("pep_relacion") || "ninguna") as RelacionPep,
            nombre: campoNodo("pep_nombre"),
            cargo: campoNodo("pep_cargo"),
            vinculo: campoNodo("pep_vinculo"),
            grado: campoNodo("pep_grado"),
          }),
        }
      : {
          ...comun,
          tipo: "pm",
          denominacion: limpio(campoNodo("denominacion")),
          nombre: null,
          apellido_paterno: null,
          apellido_materno: null,
          fecha_nacimiento: null,
        };
    setNodos((prev) => [...prev, nodo]);
    setNodoCampos({});
    invalidarResultado();
  };

  const agregarArista = () => {
    const pct = aTipo === "otros_medios"
      ? 0
      : Math.min(PORCENTAJE_MAX, Math.max(0, Number(aPct) || 0));
    const arista: BcArista = {
      id: crypto.randomUUID(),
      origen_nodo_id: aOrigen,
      destino_nodo_id: aDestino,
      participacion: pct,
      tipo_control: aTipo,
    };
    setAristas((prev) => [...prev, arista]);
    setAOrigen("");
    setADestino("");
    setAPct("");
    setATipo("participacion");
    invalidarResultado();
  };

  const quitarNodo = (id: string) => {
    setNodos((prev) => prev.filter((n) => n.id !== id));
    setAristas((prev) => prev.filter((a) => a.origen_nodo_id !== id && a.destino_nodo_id !== id));
    invalidarResultado();
  };
  const quitarArista = (id: string) => {
    setAristas((prev) => prev.filter((a) => a.id !== id));
    invalidarResultado();
  };

  // Funde la relación con PEP capturada en el nodo (persona física). Se persiste
  // al guardar el grafo; invalida el resultado para forzar re-guardar y re-derivar.
  const aplicarPepNodo = (id: string, campos: CamposRelacionPep) => {
    setNodos((prev) => prev.map((n) => (n.id === id ? { ...n, ...campos } : n)));
    setPepAbiertoId(null);
    invalidarResultado();
  };

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    // Relación PEP DERIVADA de la empresa: controlada_por_pep si algún nodo PF del
    // grafo está bajo régimen PEP. En backend real es lo que el trigger de 0067
    // materializa; aquí se reporta al padre para reflejarlo de inmediato (y en demo
    // es la única vía, porque no corre el trigger).
    const derivada: RelacionPep = nodos.some(
      (n) => n.tipo === "pf" && (n.relacion_pep ?? "ninguna") !== "ninguna",
    )
      ? "controlada_por_pep"
      : "ninguna";
    try {
      if (demo) {
        setBeneficiarios([]);
        setGuardado(0);
        onEmpresaRelacionPep?.(derivada);
        return;
      }
      const r = await guardarGrafoBc(token, persona.canonical_id, nodos, aristas);
      setBeneficiarios(r.beneficiarios);
      setGuardado(r.beneficiarios.length);
      onEmpresaRelacionPep?.(derivada);
      try {
        await onGuardado?.();
      } catch {
        // Refresco best-effort del expediente del padre: el grafo ya quedó
        // guardado; si el refresco falla, los chips se actualizan al reabrir.
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar la estructura de control.");
    } finally {
      setGuardando(false);
    }
  };

  if (!esControlable) return null;

  return (
    <>
      <div style={st.tarjeta}>
      <p style={st.eyebrow}>Estructura de control (beneficiario controlador)</p>
      <p style={{ margin: "0 0 4px", fontSize: 12.5, color: C.muted, lineHeight: 1.45 }}>
        Captura la cadena de propiedad hasta las personas físicas. Al guardar, el sistema
        marca a quién resulta beneficiario controlador: participación efectiva mayor a 25% o
        control por otros medios (Art. 18 fr. III LFPIORPI). Sigue los tres pasos.
      </p>

      {cargando ? (
        <p style={{ color: C.muted, fontSize: 13 }}>Cargando estructura…</p>
      ) : (
        <>
          {/* PASO 1 — personas de la cadena */}
          <PasoBc numero="1" titulo="Agrega las personas de la cadena">
            Da de alta las personas físicas (socios) y las empresas intermedias que controlan al
            cliente. El cliente (la raíz) ya está incluido; no lo agregues de nuevo. En cada
            persona física marca su <strong>relación con PEP</strong> (es PEP, familiar o
            allegado): de ahí se deriva la diligencia reforzada de la empresa.
          </PasoBc>
          {nodos.map((n) => {
            const relNodo: RelacionPep = n.relacion_pep ?? "ninguna";
            const esPf = n.tipo === "pf";
            const pepAbierto = pepAbiertoId === n.id;
            return (
              <div key={n.id} style={{ borderBottom: `1px solid ${C.line}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0" }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: C.ink }}>
                    {nombreNodo(n)}
                    {n.rfc !== null && n.rfc !== "" && (
                      <span style={{ fontWeight: 500, color: C.muted }}> · {n.rfc}</span>
                    )}
                  </span>
                  {relNodo !== "ninguna" && <Chip tono="rojo">{RELACION_PEP_CHIP[relNodo]}</Chip>}
                  {n.es_raiz
                    ? <Chip tono="tiel">Cliente (raíz)</Chip>
                    : <Chip tono="gris">{TIPO_ETIQUETA[n.tipo]}</Chip>}
                  {esPf && (
                    <button
                      type="button"
                      onClick={() => { setPepAbiertoId(pepAbierto ? null : n.id); }}
                      style={{ border: "none", background: "transparent", color: C.tielDark, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
                    >
                      {pepAbierto ? "Cerrar" : relNodo === "ninguna" ? "Relación con PEP" : "Editar PEP"}
                    </button>
                  )}
                  {!n.es_raiz && (
                    <button
                      type="button"
                      onClick={() => { quitarNodo(n.id); }}
                      style={{ border: "none", background: "transparent", color: C.red, fontSize: 12.5, cursor: "pointer" }}
                    >
                      Quitar
                    </button>
                  )}
                </div>
                {esPf && pepAbierto && (
                  <div style={{ padding: "0 0 10px" }}>
                    <NodoPepEditor
                      nodo={n}
                      onAplicar={(campos) => { aplicarPepNodo(n.id, campos); }}
                      onCancelar={() => { setPepAbiertoId(null); }}
                    />
                  </div>
                )}
              </div>
            );
          })}

          {/* Alta de nodo */}
          <div style={{ marginTop: 12 }}>
            <p style={st.eyebrow}>Agregar persona a la cadena</p>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {(["pf", "pm"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setNodoTipo(t); }}
                  style={{
                    ...st.btn,
                    borderColor: nodoTipo === t ? C.tiel : C.line,
                    background: nodoTipo === t ? C.tielSoft : "#fff",
                    color: nodoTipo === t ? C.tielDark : C.ink,
                  }}
                >
                  {TIPO_ETIQUETA[t]}
                </button>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {nodoTipo === "pf" ? (
                <>
                  <CampoTexto etiqueta="Nombre(s)" ancho="medio" valor={campoNodo("nombre")} onCambio={(v) => { setCampoNodo("nombre", v); }} />
                  <CampoTexto etiqueta="Apellido paterno" ancho="medio" valor={campoNodo("apellido_paterno")} onCambio={(v) => { setCampoNodo("apellido_paterno", v); }} />
                  <CampoTexto etiqueta="Apellido materno" ancho="medio" valor={campoNodo("apellido_materno")} onCambio={(v) => { setCampoNodo("apellido_materno", v); }} />
                  <CampoTexto etiqueta="RFC" ancho="medio" maxLength={LONGITUD_RFC_FISICA} normaliza={(v) => codigoAlfanumerico(v, LONGITUD_RFC_FISICA)} valor={campoNodo("rfc")} onCambio={(v) => { setCampoNodo("rfc", v); }} error={errorNodoRfc} />
                </>
              ) : (
                <>
                  <CampoTexto etiqueta="Denominación / razón social" valor={campoNodo("denominacion")} onCambio={(v) => { setCampoNodo("denominacion", v); }} />
                  <CampoTexto etiqueta="RFC" ancho="medio" maxLength={LONGITUD_RFC_MORAL} normaliza={(v) => codigoAlfanumerico(v, LONGITUD_RFC_MORAL)} valor={campoNodo("rfc")} onCambio={(v) => { setCampoNodo("rfc", v); }} error={errorNodoRfc} />
                </>
              )}
            </div>
            {/* La relación con PEP se captura aquí mismo, en el alta de la persona
                física, reusando el MISMO selector (0066) que el editor post-alta. Así
                el nodo nace ya marcado, sin agregar y luego buscar la fila para
                editarla. Persona moral no lo muestra (un nodo PM no puede ser PEP). */}
            {nodoTipo === "pf" && (
              <div style={{ marginTop: 10 }}>
                <SelectorRelacionPep campo={campoNodo} setCampo={setCampoNodo} errores={erroresPepNodo} />
              </div>
            )}
            <button
              type="button"
              style={{ ...st.btn, marginTop: 8, opacity: nodoValido ? 1 : OPACIDAD_GUARDANDO }}
              disabled={!nodoValido}
              onClick={agregarNodo}
            >
              ＋ Agregar persona
            </button>
          </div>

          {/* PASO 2 — relaciones de control */}
          <PasoBc numero="2" titulo="Define quién controla a quién">
            Por cada relación indica el controlador (el dueño), el controlado, la vía de control
            y el porcentaje de participación.
          </PasoBc>
          <div>
            {aristas.length === 0 && (
              <p style={{ color: C.muted, fontSize: 12.5, margin: "0 0 8px" }}>
                Sin relaciones aún — agrega quién controla a quién.
              </p>
            )}
            {aristas.map((a) => (
              <div
                key={a.id}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.line}` }}
              >
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: C.ink }}>{textoArista(a, nodoPorId)}</span>
                <button
                  type="button"
                  onClick={() => { quitarArista(a.id); }}
                  style={{ border: "none", background: "transparent", color: C.red, fontSize: 12.5, cursor: "pointer" }}
                >
                  Quitar
                </button>
              </div>
            ))}
          </div>

          {/* Alta de arista */}
          <div style={{ marginTop: 10 }}>
            <p style={st.eyebrow}>Agregar relación (quién controla a quién)</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ gridColumn: "span 1" }}>
                <label style={st.label}>Controlador (origen)</label>
                <select style={st.input} value={aOrigen} onChange={(e) => { setAOrigen(e.target.value); }}>
                  <option value="">Selecciona…</option>
                  {nodos.map((n) => <option key={n.id} value={n.id}>{nombreNodo(n)}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: "span 1" }}>
                <label style={st.label}>Controlado (destino)</label>
                <select style={st.input} value={aDestino} onChange={(e) => { setADestino(e.target.value); }}>
                  <option value="">Selecciona…</option>
                  {nodos.map((n) => <option key={n.id} value={n.id}>{nombreNodo(n)}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: "span 1" }}>
                <label style={st.label}>Vía de control</label>
                <select style={st.input} value={aTipo} onChange={(e) => { setATipo(e.target.value as TipoControlBc); }}>
                  {TIPOS_CONTROL.map((t) => <option key={t} value={t}>{TIPO_CONTROL_ETIQUETA[t]}</option>)}
                </select>
              </div>
              {aTipo !== "otros_medios" && (
                <CampoTexto etiqueta="% de participación" tipo="number" ancho="medio" inputMode="decimal" valor={aPct} onCambio={(v) => { setAPct(v); }} error={errorPct} />
              )}
            </div>
            <button
              type="button"
              style={{ ...st.btn, marginTop: 8, opacity: aristaValida ? 1 : OPACIDAD_GUARDANDO }}
              disabled={!aristaValida}
              onClick={agregarArista}
            >
              ＋ Agregar relación
            </button>
          </div>

          {/* PASO 3 — guardar y detectar */}
          <PasoBc numero="3" titulo="Guarda y detecta beneficiarios">
            Hasta que guardes no se calcula el beneficiario controlador ni se cierra el requisito
            del expediente. Guarda de nuevo cada vez que cambies la cadena.
          </PasoBc>
          <div>
            <button
              type="button"
              style={{ ...st.btnPrim, opacity: guardando ? OPACIDAD_GUARDANDO : 1 }}
              disabled={guardando}
              onClick={() => { void guardar(); }}
            >
              {guardando ? "Guardando…" : "Guardar estructura y detectar beneficiarios"}
            </button>
          </div>

          {guardado !== null && !guardando && (
            <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 12, background: C.greenBg, color: C.green, fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
              {demo
                ? "✓ Estructura guardada (modo demostración). El cálculo real del beneficiario controlador corre en el backend."
                : guardado > 0
                  ? `✓ Estructura guardada — ${String(guardado)} beneficiario(s) controlador(es) detectado(s). El requisito de beneficiario controlador del expediente ya quedó cubierto.`
                  : "✓ Estructura guardada. Aún no se detecta ningún beneficiario controlador (participación efectiva mayor a 25% o control por otros medios); el requisito del expediente sigue pendiente hasta que la cadena tenga al menos uno."}
            </div>
          )}

          {beneficiarios !== null && (
            <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 12, background: C.tielSoft }}>
              <p style={{ ...st.eyebrow, color: C.tielDark }}>Beneficiarios controladores detectados</p>
              {demo ? (
                <p style={{ color: C.muted, fontSize: 12.5, margin: 0 }}>
                  Modo demostración: el cálculo del beneficiario controlador corre en el backend.
                </p>
              ) : beneficiarios.length === 0 ? (
                <p style={{ color: C.muted, fontSize: 12.5, margin: 0 }}>
                  No se detectó ningún beneficiario controlador (participación efectiva mayor a 25% o
                  control por otros medios). Revisa la cadena capturada.
                </p>
              ) : (
                beneficiarios.map((b) => (
                  <div
                    key={b.nodo_id}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0" }}
                  >
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: C.ink }}>
                      {b.nombre ?? "(sin nombre)"}
                      {b.rfc !== null && b.rfc !== "" && (
                        <span style={{ fontWeight: 500, color: C.muted }}> · {b.rfc}</span>
                      )}
                    </span>
                    {b.por_otros_medios
                      ? <Chip tono="tiel">Otros medios</Chip>
                      : <Chip tono="verde">{b.participacion_efectiva}% efectivo</Chip>}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {error !== null && (
        <div style={{ marginTop: 10, padding: "9px 12px", borderRadius: 10, background: C.redBg, color: C.red, fontSize: 12.5 }}>
          {error}
        </div>
      )}
      </div>
      {!cargando && <VistaEstructura nodos={nodos} aristas={aristas} beneficiarios={beneficiarios} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Vista de estructura consolidada (solo lectura) — el "expediente de riesgo de un
// vistazo": separa el ANÁLISIS de la CAPTURA (lo que pidió el dueño). Dibuja el
// árbol de control y pinta por nodo si es beneficiario controlador y si es PEP.
// ─────────────────────────────────────────────────────────────────────────────

/** Sangría (px) por nivel de anidamiento del árbol de control. */
const INDENT_ARBOL = 20;

/** Etiqueta de la vía/porcentaje de una arista para el árbol ("posee 40%" / "otros medios"). */
function etiquetaControl(a: BcArista): string {
  if (a.tipo_control === "otros_medios") return "controla por otros medios";
  const via = a.tipo_control === "derechos_voto" ? " de derechos de voto" : "";
  return `posee ${String(a.participacion)}%${via}`;
}

/** Fila de un nodo en la vista de estructura: nombre + alertas (BC / PEP). Solo lectura. */
function FilaArbol({ nodo, arista, esBc }: { nodo: BcNodo; arista: BcArista | null; esBc: boolean }) {
  const rel: RelacionPep = nodo.relacion_pep ?? "ninguna";
  return (
    <div style={{ padding: "6px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>{nombreNodo(nodo)}</span>
        <Chip tono={nodo.es_raiz ? "tiel" : "gris"}>{nodo.es_raiz ? "Cliente (raíz)" : TIPO_ETIQUETA[nodo.tipo]}</Chip>
        {esBc && <Chip tono="verde">Beneficiario controlador</Chip>}
        {rel !== "ninguna" && <Chip tono="rojo">{RELACION_PEP_CHIP[rel]}</Chip>}
      </div>
      {arista !== null && (
        <p style={{ margin: "2px 0 0", fontSize: 11.5, color: C.muted }}>{etiquetaControl(arista)}</p>
      )}
    </div>
  );
}

/**
 * Vista de estructura consolidada (solo lectura). Raíz = empresa arriba; sus
 * controladores anidados debajo (origen → destino = "origen posee/controla
 * destino"). Por nodo pinta si resultó beneficiario controlador (del cálculo del
 * backend, `bc_guardar_grafo`/`bc_beneficiarios`) y si es PEP/familiar/allegado
 * (del propio nodo). No captura nada: el REC lee el riesgo sin recorrer formularios.
 */
export function VistaEstructura({
  nodos,
  aristas,
  beneficiarios,
}: {
  nodos: BcNodo[];
  aristas: BcArista[];
  beneficiarios: BcBeneficiario[] | null;
}) {
  const nodoPorId = useMemo(
    () => new Map<string, BcNodo>(nodos.map((n): [string, BcNodo] => [n.id, n])),
    [nodos],
  );
  const controladores = useMemo(() => {
    const m = new Map<string, BcArista[]>();
    for (const a of aristas) {
      const xs = m.get(a.destino_nodo_id) ?? [];
      xs.push(a);
      m.set(a.destino_nodo_id, xs);
    }
    return m;
  }, [aristas]);
  const beneficiarioIds = useMemo(
    () => new Set<string>((beneficiarios ?? []).map((b) => b.nodo_id)),
    [beneficiarios],
  );

  const raiz = nodos.find((n) => n.es_raiz) ?? nodos[0];

  // Nodos alcanzables desde la raíz siguiendo las relaciones de control. Lo que
  // quede fuera se lista aparte (huérfano) para no ocultar nada del expediente.
  const alcanzables = useMemo(() => {
    const vistos = new Set<string>();
    const pila: string[] = raiz !== undefined ? [raiz.id] : [];
    while (pila.length > 0) {
      const id = pila.pop();
      if (id === undefined || vistos.has(id)) continue;
      vistos.add(id);
      for (const a of controladores.get(id) ?? []) pila.push(a.origen_nodo_id);
    }
    return vistos;
  }, [raiz, controladores]);

  const huerfanos = nodos.filter((n) => !alcanzables.has(n.id) && !n.es_raiz);
  const totalPf = nodos.filter((n) => n.tipo === "pf").length;
  const conPep = nodos.filter((n) => n.tipo === "pf" && (n.relacion_pep ?? "ninguna") !== "ninguna").length;

  const renderNodo = (nodoId: string, arista: BcArista | null, visitados: ReadonlySet<string>): ReactNode => {
    const nodo = nodoPorId.get(nodoId);
    if (nodo === undefined) return null;
    if (visitados.has(nodoId)) {
      return (
        <div key={`ciclo-${nodoId}`} style={{ padding: "4px 0", fontSize: 11.5, color: C.amber }}>
          ↺ {nombreNodo(nodo)} (ya mostrado — ciclo en la cadena)
        </div>
      );
    }
    const siguientes = new Set<string>([...visitados, nodoId]);
    const hijos = controladores.get(nodoId) ?? [];
    return (
      <div key={nodoId}>
        <FilaArbol nodo={nodo} arista={arista} esBc={beneficiarioIds.has(nodoId)} />
        {hijos.length > 0 && (
          <div style={{ marginLeft: INDENT_ARBOL, borderLeft: `1px solid ${C.line}`, paddingLeft: 12 }}>
            {hijos.map((a) => renderNodo(a.origen_nodo_id, a, siguientes))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={st.tarjeta}>
      <p style={st.eyebrow}>Vista de estructura — expediente de riesgo de un vistazo</p>
      <p style={{ margin: "0 0 10px", fontSize: 12.5, color: C.muted, lineHeight: 1.45 }}>
        Solo lectura: la cadena de control con sus alertas. Cada nodo marca si resultó
        beneficiario controlador (participación efectiva mayor a 25% o control por otros medios) y
        si es PEP, familiar o allegado de una.
      </p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <Chip tono={conPep > 0 ? "rojo" : "gris"}>
          {conPep > 0
            ? `${String(conPep)} de ${String(totalPf)} personas físicas bajo régimen PEP`
            : "Sin PEP en la estructura"}
        </Chip>
        <Chip tono={beneficiarios === null ? "ambar" : beneficiarios.length > 0 ? "verde" : "gris"}>
          {beneficiarios === null
            ? "Beneficiarios: guarda para calcular"
            : `${String(beneficiarios.length)} beneficiario(s) controlador(es)`}
        </Chip>
      </div>

      {raiz === undefined
        ? <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>Aún no hay estructura capturada.</p>
        : renderNodo(raiz.id, null, new Set<string>())}

      {huerfanos.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <p style={{ ...st.eyebrow, color: C.amber }}>Sin conectar a la raíz</p>
          <p style={{ margin: "0 0 6px", fontSize: 11.5, color: C.muted }}>
            Estas personas están capturadas pero no tienen una relación de control que las ligue al
            cliente. Agrega la relación en la captura para que cuenten en el análisis.
          </p>
          {huerfanos.map((n) => <FilaArbol key={n.id} nodo={n} arista={null} esBc={beneficiarioIds.has(n.id)} />)}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Vista principal
// ─────────────────────────────────────────────────────────────────────────────

export function PersonasView() {
  const { session } = useAuth();
  const token = session?.accessToken ?? "";
  const tenantId = session?.tenantId ?? "";
  const demo = !apiConfigurada() || token === "";

  const [personas, setPersonas] = useState<PersonaRow[]>(demo ? PERSONAS_DEMO : []);
  const [filtro, setFiltro] = useState("");
  const [selId, setSelId] = useState<string | null>(null);
  const [modo, setModo] = useState<"detalle" | "nueva">("detalle");
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(!demo);
  const [expediente, setExpediente] = useState<RequisitoEstado[] | null>(null);
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const [demoDocs, setDemoDocs] = useState<Record<string, string[]>>({});
  const [actividadesPf, setActividadesPf] = useState<CatalogoOption[]>(ACTIVIDADES_PF_FALLBACK);
  const [girosPm, setGirosPm] = useState<CatalogoOption[]>(GIROS_PM_FALLBACK);

  const sel = personas.find((p) => p.id === selId) ?? null;

  const recargarPersonas = useCallback(async () => {
    if (demo) return;
    const filas = await listarPersonas(token);
    setPersonas(filas);
  }, [demo, token]);

  useEffect(() => {
    if (demo) return;
    setCargando(true);
    listarPersonas(token)
      .then((filas) => { setPersonas(filas); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudo cargar la lista."); })
      .finally(() => { setCargando(false); });
  }, [demo, token]);

  useEffect(() => {
    if (demo) {
      setActividadesPf(ACTIVIDADES_PF_FALLBACK);
      setGirosPm(GIROS_PM_FALLBACK);
      return;
    }
    Promise.all([
      listarCatalogo(token, "cat_actividad_giro_pld", "&tipo_persona=eq.pf"),
      listarCatalogo(token, "cat_actividad_giro_pld", "&tipo_persona=eq.pm"),
    ])
      .then(([pf, pm]) => {
        setActividadesPf(opcionesCatalogo(pf));
        setGirosPm(opcionesCatalogo(pm));
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "No se pudieron cargar los catálogos de actividad/giro.");
      });
  }, [demo, token]);

  const cargarExpediente = useCallback(
    async (p: PersonaRow) => {
      if (demo) {
        setExpediente(estadoDemo(p, new Set(demoDocs[p.id] ?? [])));
        return;
      }
      setExpediente(null);
      const filas = await expedienteEstado(token, p.id);
      setExpediente(filas);
    },
    [demo, demoDocs, token],
  );

  useEffect(() => {
    if (sel && modo === "detalle") {
      cargarExpediente(sel).catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "No se pudo cargar el expediente.");
      });
    }
  }, [sel, modo, cargarExpediente]);

  const subirDoc = async (clave: string, archivo: File) => {
    if (!sel) return;
    setSubiendo(clave);
    setError(null);
    try {
      if (demo) {
        setDemoDocs((d) => ({ ...d, [sel.id]: [...(d[sel.id] ?? []), clave] }));
        const nuevas = new Set([...(demoDocs[sel.id] ?? []), clave]);
        const estado = estadoDemo(sel, nuevas);
        const completo = !estado.some((r) => r.obligatorio && !nuevas.has(r.clave));
        setPersonas((ps) => ps.map((p) => (p.id === sel.id ? { ...p, expediente_completo: completo } : p)));
        setExpediente(estado.map((r) => ({ ...r, satisfecho: nuevas.has(r.clave) })));
      } else {
        await subirDocumentoExpediente({ token, tenantId, clienteId: sel.id, requisitoClave: clave, archivo });
        await cargarExpediente(sel);
        await recargarPersonas();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo subir el documento.");
    } finally {
      setSubiendo(null);
    }
  };

  const cambiarRiesgo = async (patch: PatchRiesgoPep) => {
    if (!sel) return;
    setError(null);
    try {
      if (demo) {
        const actualizada: PersonaRow = { ...sel, ...patch };
        // El backend deriva estatus_pep de relacion_pep con un trigger; en demo lo
        // reconciliamos a mano para que otras lecturas del flag legado sean fieles.
        if (patch.relacion_pep !== undefined) actualizada.estatus_pep = patch.relacion_pep === "es_pep";
        setPersonas((ps) => ps.map((p) => (p.id === sel.id ? actualizada : p)));
        setExpediente(estadoDemo(actualizada, new Set(demoDocs[sel.id] ?? [])));
      } else {
        await actualizarPersona(token, sel.id, patch);
        await recargarPersonas();
        const refrescada = { ...sel, ...patch };
        await cargarExpediente(refrescada);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo actualizar.");
    }
  };

  /**
   * Refleja de inmediato la relación PEP DERIVADA de la empresa (0067) al guardar
   * su grafo de control. En backend real el trigger la materializa y el refresco
   * (onGrafoBcGuardado → recargarPersonas) la confirma; en demo es la única vía.
   * La empresa no ES PEP: estatus_pep queda en false.
   */
  const derivarPepEmpresa = (rel: RelacionPep) => {
    if (!sel) return;
    setPersonas((ps) => ps.map((p) => (p.id === sel.id ? { ...p, relacion_pep: rel, estatus_pep: false } : p)));
  };

  /**
   * Subsanación (issue #32): completa los datos del representante/apoderado en un
   * expediente ya existente. Antes la edición sólo permitía estatus_pep/nivel_riesgo,
   * así que un cliente PM/fideicomiso capturado incompleto no tenía camino de arreglo
   * y el XML tronaba al generarse. Persiste el mismo JSONB `representante`.
   */
  const guardarRepresentante = async (representante: Record<string, string> | null) => {
    if (!sel) return;
    setError(null);
    try {
      if (demo) {
        setPersonas((ps) => ps.map((p) => (p.id === sel.id ? { ...p, representante } : p)));
      } else {
        await actualizarPersona(token, sel.id, { representante });
        await recargarPersonas();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar el representante.");
    }
  };

  const filtradas = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (q === "") return personas;
    return personas.filter((p) =>
      [nombrePersona(p), p.rfc ?? "", TIPO_ETIQUETA[p.tipo] ?? ""].some((s) => s.toLowerCase().includes(q)),
    );
  }, [personas, filtro]);

  return (
    <div className="argus-master-detail" style={{ display: "flex", flex: 1, minWidth: 0 }}>
      {/* Columna central: lista */}
      <section
        className="argus-list-pane"
        style={{
          width: 330,
          flexShrink: 0,
          borderRight: `1px solid ${C.line}`,
          display: "flex",
          flexDirection: "column",
          background: "#FBFCFB",
        }}
      >
        <div style={{ padding: "16px 16px 10px" }}>
          <h2 style={{ ...st.display, margin: 0, fontSize: 18 }}>Personas</h2>
          <p style={{ margin: "2px 0 10px", fontSize: 12, color: C.muted }}>
            Un expediente por persona, reutilizable en captación y compraventas.
          </p>
          <input
            style={st.input}
            placeholder="Buscar por nombre, RFC o tipo…"
            value={filtro}
            onChange={(e) => { setFiltro(e.target.value); }}
          />
        </div>
        {demo && (
          <div style={{ margin: "0 16px 8px", padding: "6px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 11.5, fontWeight: 600 }}>
            Modo demostración — sin backend configurado; datos en memoria.
          </div>
        )}
        <div style={{ flex: 1, overflow: "auto", padding: "0 12px 8px" }}>
          {cargando && <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>Cargando…</p>}
          {!cargando && filtradas.length === 0 && (
            <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>Sin personas todavía — crea la primera.</p>
          )}
          {filtradas.map((p) => {
            const activa = selId === p.id && modo === "detalle";
            return (
              <button
                key={p.id}
                onClick={() => { setSelId(p.id); setModo("detalle"); setError(null); }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "#fff",
                  border: `1px solid ${activa ? C.tiel : C.line}`,
                  boxShadow: activa ? `0 0 0 1px ${C.tiel}` : "none",
                  borderRadius: 12,
                  padding: "10px 12px",
                  marginBottom: 8,
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13.5, color: C.ink }}>{nombrePersona(p)}</div>
                <div style={{ fontSize: 12, color: C.muted, margin: "2px 0 6px" }}>
                  {TIPO_ETIQUETA[p.tipo]}{p.rfc ? ` · ${p.rfc}` : ""}
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {p.expediente_completo
                    ? <Chip tono="verde">✓ Expediente completo</Chip>
                    : <Chip tono="ambar">Expediente incompleto</Chip>}
                  {p.relacion_pep !== "ninguna" && <Chip tono="rojo">{RELACION_PEP_CHIP[p.relacion_pep]}</Chip>}
                  {p.nivel_riesgo === "alto" && <Chip tono="rojo">Riesgo alto</Chip>}
                </div>
              </button>
            );
          })}
        </div>
        <div style={{ padding: 12, borderTop: `1px solid ${C.line}` }}>
          <button
            style={{ ...st.btnPrim, width: "100%" }}
            onClick={() => { setModo("nueva"); setSelId(null); setError(null); }}
          >
            ＋ Nueva persona
          </button>
        </div>
      </section>

      {/* Panel derecho: detalle o captura */}
      <main className="argus-detail-pane" style={{ flex: 1, overflow: "auto", padding: "22px 26px", minWidth: 0 }}>
        {error && (
          <div style={{ maxWidth: 560, margin: "0 auto 12px", padding: "10px 14px", borderRadius: 10, background: C.redBg, color: C.red, fontSize: 13 }}>
            {error}
          </div>
        )}
        {modo === "nueva" ? (
          <FormNuevaPersona
            demo={demo}
            actividadesPf={actividadesPf}
            girosPm={girosPm}
            onCancelar={() => { setModo("detalle"); }}
            onCrear={async (datos) => {
              setError(null);
              try {
                if (demo) {
                  const nueva = personaVacia(datos);
                  setPersonas((ps) => [nueva, ...ps]);
                  setSelId(nueva.id);
                } else {
                  const fila = await crearPersona(token, tenantId, datos);
                  await recargarPersonas();
                  setSelId(fila.id);
                }
                setModo("detalle");
              } catch (e) {
                setError(e instanceof Error ? e.message : "No se pudo crear la persona.");
              }
            }}
          />
        ) : sel ? (
          <DetallePersona
            p={sel}
            token={token}
            demo={demo}
            expediente={expediente}
            subiendo={subiendo}
            onSubir={(clave, archivo) => { void subirDoc(clave, archivo); }}
            onCambiarRiesgo={(patch) => { void cambiarRiesgo(patch); }}
            onGuardarRepresentante={(rep) => { void guardarRepresentante(rep); }}
            onEmpresaRelacionPep={derivarPepEmpresa}
            onGrafoBcGuardado={async () => {
              // Tras guardar el grafo BC, refresca lista y expediente para que los
              // chips ("Grafo de control pendiente" → "BC detectado") se actualicen.
              await recargarPersonas();
              await cargarExpediente(sel);
            }}
          />
        ) : (
          <div style={{ maxWidth: 420, margin: "18vh auto 0", textAlign: "center", color: C.muted }}>
            <div style={{ marginBottom: 8, color: C.tielDark }}><IconoPersonas size={34} /></div>
            <h3 style={{ margin: "0 0 6px", color: C.ink }}>Selecciona una persona</h3>
            <p style={{ fontSize: 13.5, margin: 0 }}>
              Elige una de la lista para ver su expediente, o crea una nueva. La ley obliga a
              identificar desde la primera aportación, sin importar el monto.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Detalle: datos + expediente por requisitos (cuestionario condicionado)
// ─────────────────────────────────────────────────────────────────────────────

function DetallePersona({
  p,
  token,
  demo,
  expediente,
  subiendo,
  onSubir,
  onCambiarRiesgo,
  onGuardarRepresentante,
  onEmpresaRelacionPep,
  onGrafoBcGuardado,
}: {
  p: PersonaRow;
  token: string;
  demo: boolean;
  expediente: RequisitoEstado[] | null;
  subiendo: string | null;
  onSubir: (clave: string, archivo: File) => void;
  onCambiarRiesgo: (patch: PatchRiesgoPep) => void;
  onGuardarRepresentante: (representante: Record<string, string> | null) => void;
  onEmpresaRelacionPep: (rel: RelacionPep) => void;
  onGrafoBcGuardado: () => Promise<void>;
}) {
  const esEmpresa = p.tipo === "pm" || p.tipo === "fideicomiso";
  // Resumen PEP del grafo (lo produce EstructuraControlBc, abajo) para el apartado
  // derivado de la empresa. Se reinicia al cambiar de persona.
  const [resumenPep, setResumenPep] = useState<ResumenPepGrafo | null>(null);
  const reportarResumenPep = useCallback((r: ResumenPepGrafo) => { setResumenPep(r); }, []);
  useEffect(() => { setResumenPep(null); }, [p.id]);

  const dom = p.domicilio;
  const con = p.contacto;
  const domTexto = ["calle", "numero_exterior", "colonia", "codigo_postal"]
    .map((k) => { const v = dom[k]; return typeof v === "string" ? v : ""; })
    .filter((s) => s !== "")
    .join(", ");
  const numeroTelefono = contactoValor(con, "numero_telefono", "telefono");
  const clavePais = contactoValor(con, "clave_pais") ?? "MX";
  const correoElectronico = contactoValor(con, "correo_electronico", "correo");

  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 2px", fontSize: 20 }}>{nombrePersona(p)}</h2>
      <p style={{ margin: "0 0 10px", color: C.muted, fontSize: 13 }}>{TIPO_ETIQUETA[p.tipo]}</p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {p.expediente_completo
          ? <Chip tono="verde">✓ Expediente completo</Chip>
          : <Chip tono="ambar">Expediente incompleto — la operación no se libera</Chip>}
        {p.relacion_pep !== "ninguna" && (
          <Chip tono="rojo">{RELACION_PEP_CHIP[p.relacion_pep]} — diligencia reforzada</Chip>
        )}
        <Chip tono={p.nivel_riesgo === "alto" ? "rojo" : "gris"}>Riesgo: {p.nivel_riesgo}</Chip>
      </div>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Datos de identificación</p>
        <TablaDatos
          filas={[
            ["RFC", p.rfc],
            ["CURP", p.curp],
            ...(p.tipo === "pf"
              ? ([
                  ["Fecha de nacimiento", p.fecha_nacimiento ? fecha(p.fecha_nacimiento) : null],
                  ["País de nacionalidad", p.pais_nacionalidad],
                  ["Actividad / ocupación", p.actividad_economica],
                ] as [string, string | null][])
              : []),
            ...(p.tipo === "pm"
              ? ([
                  ["Fecha de constitución", p.fecha_constitucion ? fecha(p.fecha_constitucion) : null],
                  ["Giro mercantil", p.giro_mercantil],
                ] as [string, string | null][])
              : []),
            ...(p.tipo === "fideicomiso"
              ? ([
                  ["Fiduciario", p.fiduciario_denominacion],
                  ["Identificador", p.fideicomiso_identificador],
                ] as [string, string | null][])
              : []),
            ["Domicilio", domTexto === "" ? null : domTexto],
            ["Teléfono", numeroTelefono ? `${clavePais} ${numeroTelefono}` : null],
            ["Correo", correoElectronico],
          ]}
        />
      </div>

      {/* Apartado "Relación con PEP" del cliente, por tipo (0066 + 0067):
          - PF (y "otra"): captura DIRECTA con reversión visible (EditorRelacionPep).
          - PM/fideicomiso: DERIVADO y solo lectura (ApartadoPepEmpresa) — la empresa
            no se marca PEP a mano; su estatus lo materializa el backend del grafo.
          El `key` re-siembra el borrador de PF al cambiar de persona. */}
      {esEmpresa
        ? <ApartadoPepEmpresa key={`pep-emp-${p.id}`} persona={p} resumen={resumenPep} />
        : <EditorRelacionPep key={`pep-${p.id}`} persona={p} onGuardar={(campos) => { onCambiarRiesgo(campos); }} />}

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Perfil de riesgo (recalcula el cuestionario)</p>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5 }}>
          Nivel de riesgo
          <select
            style={{ ...st.input, width: "auto", padding: "6px 10px" }}
            value={p.nivel_riesgo}
            onChange={(e) => { onCambiarRiesgo({ nivel_riesgo: e.target.value }); }}
          >
            <option value="pendiente">pendiente</option>
            <option value="bajo">bajo</option>
            <option value="medio">medio</option>
            <option value="alto">alto</option>
          </select>
        </label>
        <p style={{ margin: "8px 0 0", fontSize: 12, color: C.muted }}>
          El nivel de riesgo alto activa por sí solo los requisitos de diligencia reforzada
          (origen de recursos, comprobante de ingresos…). La relación con PEP se captura en su
          propio apartado, arriba. El expediente se re-evalúa solo.
        </p>
      </div>

      {(p.tipo === "pm" || p.tipo === "fideicomiso") && (
        <EditorRepresentante
          key={p.id}
          titulo={p.tipo === "pm" ? "Representante o apoderado" : "Apoderado o delegado fiduciario"}
          representante={p.representante}
          onGuardar={onGuardarRepresentante}
        />
      )}

      {esEmpresa && (
        <EstructuraControlBc
          key={`bc-${p.id}`}
          persona={p}
          token={token}
          demo={demo}
          onGuardado={onGrafoBcGuardado}
          onResumenPep={reportarResumenPep}
          onEmpresaRelacionPep={onEmpresaRelacionPep}
        />
      )}

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Expediente — requisitos que aplican a esta persona</p>
        {expediente === null && <p style={{ color: C.muted, fontSize: 13 }}>Cargando expediente…</p>}
        {expediente !== null && expediente.length === 0 && (
          <p style={{ color: C.muted, fontSize: 13 }}>El catálogo no tiene requisitos para este tipo.</p>
        )}
        {expediente?.map((r) => (
          <div
            key={r.clave}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 0",
              borderBottom: `1px solid ${C.line}`,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 20,
                height: 20,
                borderRadius: 6,
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "#fff",
                background: r.satisfecho ? C.green : "#D6DBDA",
              }}
            >
              {r.satisfecho ? "✓" : ""}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>
                {r.nombre}
                {!r.obligatorio && <span style={{ fontWeight: 500, color: C.muted }}> · recomendado</span>}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
                {r.es_ddr && <Chip tono="rojo">Diligencia reforzada</Chip>}
                {r.documento_id && <Chip tono="verde">Documento en bóveda</Chip>}
                {r.requiere_grafo_bc && (
                  r.grafo_bc_resuelto
                    ? <Chip tono="verde">Grafo de control: BC detectado</Chip>
                    : <Chip tono="ambar">Grafo de control pendiente</Chip>
                )}
              </div>
              {r.requiere_grafo_bc && !r.grafo_bc_resuelto && (
                <p style={{ margin: "4px 0 0", fontSize: 11.5, color: C.muted }}>
                  El documento respalda, no sustituye: captura la cadena de control
                  (beneficiarios &gt;25%) para cerrar este requisito.
                </p>
              )}
            </div>
            {!r.documento_id && (
              <label style={{ ...st.btn, flexShrink: 0, opacity: subiendo === r.clave ? OPACIDAD_OCUPADO : 1 }}>
                {subiendo === r.clave ? "Subiendo…" : "Subir documento"}
                <input
                  type="file"
                  style={{ display: "none" }}
                  disabled={subiendo !== null}
                  onChange={(e) => {
                    const archivo = e.target.files?.[0];
                    if (archivo) onSubir(r.clave, archivo);
                    e.target.value = "";
                  }}
                />
              </label>
            )}
          </div>
        ))}
        <p style={{ margin: "10px 0 0", fontSize: 12, color: C.muted }}>
          Cada archivo se guarda con su huella SHA-256 y sello de tiempo, y se conserva 10
          años (bóveda). La completitud la deriva el sistema — no es un checkbox manual.
        </p>
        {(p.tipo === "pm" || p.tipo === "fideicomiso") && (
          <p style={{ margin: "8px 0 0", fontSize: 11.5, color: C.muted }}>
            Nota: el beneficiario controlador que aquí se identifica es para el aviso a la
            UIF (LFPIORPI art. 18). Es distinto de la obligación fiscal del art. 32-B del
            CFF —en la que el propio contribuyente informa sus beneficiarios controladores
            al SAT—: comparten definición, no son el mismo trámite.
          </p>
        )}
      </div>
    </div>
  );
}

function TablaDatos({ filas }: { filas: [string, string | null][] }) {
  return (
    <div>
      {filas.map(([k, v]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13.5, padding: "3px 0" }}>
          <span style={{ color: C.muted }}>{k}</span>
          <span style={{ fontWeight: 600, textAlign: "right", color: v ? C.ink : "#B7BEBC" }}>{v ?? "—"}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Alta de persona — formulario por tipo (los campos que el aviso DIN pide)
// ─────────────────────────────────────────────────────────────────────────────

export function FormNuevaPersona({
  demo,
  actividadesPf,
  girosPm,
  onCrear,
  onCancelar,
}: {
  demo: boolean;
  actividadesPf: CatalogoOption[];
  girosPm: CatalogoOption[];
  onCrear: (datos: Record<string, unknown>) => Promise<void>;
  onCancelar: () => void;
}) {
  const [tipo, setTipo] = useState<TipoPersona>("pf");
  const [f, setF] = useState<Record<string, string>>({ pais: "MX", con_clave_pais: "MX", pep_relacion: "ninguna" });
  const [guardando, setGuardando] = useState(false);

  const campo = (k: string) => f[k] ?? "";
  const setCampo = (k: string, v: string) => { setF((prev) => ({ ...prev, [k]: v })); };
  const actividadOpciones = tipo === "pf" ? actividadesPf : tipo === "pm" ? girosPm : [];
  const claveEconomicaValida = tipo === "pf"
    ? (actividadesPf.length > 0 ? actividadesPf.some(([clave]) => clave === campo("actividad")) : /^\d{7}$/.test(campo("actividad")))
    : tipo === "pm"
      ? (girosPm.length > 0 ? girosPm.some(([clave]) => clave === campo("giro")) : /^\d{7}$/.test(campo("giro")))
      : true;

  const armarDatos = (): Record<string, unknown> => {
    const domicilio = armarDomicilioPersona(campo);
    const telefono = limpio(campo("con_telefono"));
    const correo = limpio(campo("con_correo"));
    const clavePais = limpio(campo("con_clave_pais")) ?? "MX";
    const contacto: Record<string, string> = {};
    if (telefono || correo) contacto.clave_pais = clavePais;
    if (telefono) contacto.numero_telefono = telefono;
    if (correo) contacto.correo_electronico = correo;
    // Fuente de verdad de la relación con PEP. NO se escribe estatus_pep: el
    // trigger de reconciliación del backend lo deriva de relacion_pep (0066).
    const pep = normalizarRelacionPep({
      relacion: (campo("pep_relacion") || "ninguna") as RelacionPep,
      nombre: campo("pep_nombre"),
      cargo: campo("pep_cargo"),
      vinculo: campo("pep_vinculo"),
      grado: campo("pep_grado"),
    });
    const base: Record<string, unknown> = {
      tipo,
      rfc: limpio(campo("rfc")),
      ...pep,
      domicilio,
      contacto,
    };
    if (tipo === "pf") {
      return {
        ...base,
        nombre: limpio(campo("nombre")),
        apellido_paterno: limpio(campo("apellido_paterno")),
        apellido_materno: limpio(campo("apellido_materno")),
        fecha_nacimiento: limpio(campo("fecha_nacimiento")),
        curp: limpio(campo("curp")),
        pais_nacionalidad: limpio(campo("pais")) ?? "MX",
        actividad_economica: limpio(campo("actividad")),
      };
    }
    if (tipo === "pm") {
      return {
        ...base,
        denominacion_razon: limpio(campo("denominacion")),
        fecha_constitucion: limpio(campo("fecha_constitucion")),
        giro_mercantil: limpio(campo("giro")),
        pais_nacionalidad: limpio(campo("pais")) ?? "MX",
        representante: armarRepresentante(campo),
      };
    }
    return {
      ...base,
      fiduciario_denominacion: limpio(campo("fiduciario")),
      fideicomiso_identificador: limpio(campo("identificador")),
      representante: armarRepresentante(campo),
    };
  };
  const erroresValidacion = erroresNuevaPersona(tipo, campo, claveEconomicaValida);
  const clavesConError = Object.keys(erroresValidacion);
  const puedeGuardar = !guardando && clavesConError.length === 0;

  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      <button onClick={onCancelar} style={{ border: "none", background: "transparent", color: C.muted, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 8 }}>
        ‹ Cancelar
      </button>
      <h2 style={{ margin: "0 0 2px", fontSize: 20 }}>Nueva persona</h2>
      <p style={{ margin: "0 0 14px", color: C.muted, fontSize: 13 }}>
        Captura una vez, usa en todo: el expediente se referencia desde captación y
        compraventas. El cuestionario de documentos aparece al guardar.
      </p>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Tipo</p>
        <div style={{ display: "flex", gap: 8 }}>
          {(["pf", "pm", "fideicomiso"] as TipoPersona[]).map((t) => (
            <button
              key={t}
              onClick={() => { setTipo(t); }}
              style={{
                ...st.btn,
                borderColor: tipo === t ? C.tiel : C.line,
                background: tipo === t ? C.tielSoft : "#fff",
                color: tipo === t ? C.tielDark : C.ink,
              }}
            >
              {TIPO_ETIQUETA[t]}
            </button>
          ))}
        </div>
      </div>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Identificación {tipo === "pf" ? "(los campos que pide el aviso)" : ""}</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {tipo === "pf" && (
            <>
              <CampoTexto etiqueta="Nombre(s)" ancho="medio" valor={campo("nombre")} onCambio={(v) => { setCampo("nombre", v); }} />
              <CampoTexto etiqueta="Apellido paterno" ancho="medio" valor={campo("apellido_paterno")} onCambio={(v) => { setCampo("apellido_paterno", v); }} />
              <CampoTexto etiqueta="Apellido materno" ancho="medio" valor={campo("apellido_materno")} onCambio={(v) => { setCampo("apellido_materno", v); }} />
              <CampoTexto etiqueta="Fecha de nacimiento" tipo="date" ancho="medio" valor={campo("fecha_nacimiento")} onCambio={(v) => { setCampo("fecha_nacimiento", v); }} />
              <CampoTexto etiqueta="RFC (13)" ancho="medio" maxLength={LONGITUD_RFC_FISICA} normaliza={(v) => codigoAlfanumerico(v, LONGITUD_RFC_FISICA)} valor={campo("rfc")} onCambio={(v) => { setCampo("rfc", v); }} error={erroresValidacion.rfc} />
              <CampoTexto etiqueta="CURP" ancho="medio" maxLength={LONGITUD_CURP} normaliza={(v) => codigoAlfanumerico(v, LONGITUD_CURP)} valor={campo("curp")} onCambio={(v) => { setCampo("curp", v); }} error={erroresValidacion.curp} />
              <CampoTexto etiqueta="País de nacionalidad (MX)" ancho="medio" maxLength={LONGITUD_PAIS} normaliza={pais2} valor={campo("pais")} onCambio={(v) => { setCampo("pais", v); }} error={erroresValidacion.pais} />
              <CampoCatalogo etiqueta="Actividad u ocupación" opciones={actividadOpciones} valor={campo("actividad")} onCambio={(v) => { setCampo("actividad", v); }} error={campo("actividad") !== "" ? erroresValidacion.actividad : undefined} />
            </>
          )}
          {tipo === "pm" && (
            <>
              <CampoTexto etiqueta="Denominación / razón social" valor={campo("denominacion")} onCambio={(v) => { setCampo("denominacion", v); }} />
              <CampoTexto etiqueta="RFC (12)" ancho="medio" maxLength={LONGITUD_RFC_MORAL} normaliza={(v) => codigoAlfanumerico(v, LONGITUD_RFC_MORAL)} valor={campo("rfc")} onCambio={(v) => { setCampo("rfc", v); }} error={erroresValidacion.rfc} />
              <CampoTexto etiqueta="Fecha de constitución" tipo="date" ancho="medio" valor={campo("fecha_constitucion")} onCambio={(v) => { setCampo("fecha_constitucion", v); }} />
              <CampoCatalogo etiqueta="Giro mercantil" opciones={actividadOpciones} valor={campo("giro")} onCambio={(v) => { setCampo("giro", v); }} error={campo("giro") !== "" ? erroresValidacion.giro : undefined} />
              <CampoTexto etiqueta="País (MX)" ancho="medio" maxLength={LONGITUD_PAIS} normaliza={pais2} valor={campo("pais")} onCambio={(v) => { setCampo("pais", v); }} error={erroresValidacion.pais} />
            </>
          )}
          {tipo === "fideicomiso" && (
            <>
              <CampoTexto etiqueta="Identificador / número (F/…)" ancho="medio" valor={campo("identificador")} onCambio={(v) => { setCampo("identificador", v); }} />
              <CampoTexto etiqueta="RFC del fideicomiso" ancho="medio" maxLength={LONGITUD_RFC_FISICA} normaliza={(v) => codigoAlfanumerico(v, LONGITUD_RFC_FISICA)} valor={campo("rfc")} onCambio={(v) => { setCampo("rfc", v); }} error={erroresValidacion.rfc} />
              <CampoTexto etiqueta="Fiduciario (institución)" valor={campo("fiduciario")} onCambio={(v) => { setCampo("fiduciario", v); }} />
            </>
          )}
        </div>
      </div>

      {tipo === "pm" && (
        <TarjetaRepresentante titulo="Representante o apoderado (se liga su expediente)" campo={campo} setCampo={setCampo} errores={erroresValidacion} />
      )}
      {tipo === "fideicomiso" && (
        <TarjetaRepresentante titulo="Apoderado o delegado fiduciario (se liga su expediente)" campo={campo} setCampo={setCampo} errores={erroresValidacion} />
      )}

      {(tipo === "pm" || tipo === "fideicomiso") && (
        <div style={{ margin: "0 0 12px", padding: "10px 14px", borderRadius: 12, background: C.blueBg, color: C.blue, fontSize: 12.5, lineHeight: 1.45 }}>
          <strong>Beneficiario controlador:</strong> se captura al abrir el expediente, después de
          crear esta persona — la cadena de control se liga a la persona ya guardada. Aquí no lo
          busques; aparece como una sección propia en el detalle.
        </div>
      )}

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Domicilio y contacto</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ gridColumn: "span 2", display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(["nacional", "extranjero"] as const).map((t) => {
              const activo = (campo("dom_tipo") || "nacional") === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setCampo("dom_tipo", t); }}
                  style={{
                    ...st.btn,
                    borderColor: activo ? C.tiel : C.line,
                    background: activo ? C.tielSoft : "#fff",
                    color: activo ? C.tielDark : C.ink,
                  }}
                >
                  {t === "nacional" ? "Nacional" : "Extranjero"}
                </button>
              );
            })}
          </div>
          {(campo("dom_tipo") === "extranjero") && (
            <>
              <CampoTexto etiqueta="País del domicilio" ancho="medio" maxLength={LONGITUD_PAIS} normaliza={pais2} valor={campo("dom_pais")} onCambio={(v) => { setCampo("dom_pais", v); }} error={erroresValidacion.dom_pais} />
              <CampoTexto etiqueta="Estado/provincia" ancho="medio" valor={campo("dom_estado_provincia")} onCambio={(v) => { setCampo("dom_estado_provincia", v); }} error={erroresValidacion.dom_estado_provincia} />
              <CampoTexto etiqueta="Ciudad/población" valor={campo("dom_ciudad")} onCambio={(v) => { setCampo("dom_ciudad", v); }} error={erroresValidacion.dom_ciudad} />
            </>
          )}
          <CampoTexto etiqueta="Calle" valor={campo("dom_calle")} onCambio={(v) => { setCampo("dom_calle", v); }} error={erroresValidacion.dom_calle} />
          <CampoTexto etiqueta="Número exterior" ancho="medio" valor={campo("dom_numero_exterior")} onCambio={(v) => { setCampo("dom_numero_exterior", v); }} error={erroresValidacion.dom_numero_exterior} />
          <CampoTexto etiqueta="Número interior" ancho="medio" valor={campo("dom_numero_interior")} onCambio={(v) => { setCampo("dom_numero_interior", v); }} />
          <CampoTexto etiqueta="Colonia" ancho="medio" valor={campo("dom_colonia")} onCambio={(v) => { setCampo("dom_colonia", v); }} error={erroresValidacion.dom_colonia} />
          <CampoTexto etiqueta="Código postal (5 dígitos)" ancho="medio" inputMode="numeric" maxLength={LONGITUD_CP} normaliza={(v) => soloDigitos(v, LONGITUD_CP)} valor={campo("dom_codigo_postal")} onCambio={(v) => { setCampo("dom_codigo_postal", v); }} error={erroresValidacion.dom_codigo_postal} />
          <CampoTexto etiqueta="Clave país" ancho="medio" maxLength={LONGITUD_PAIS} normaliza={pais2} valor={campo("con_clave_pais")} onCambio={(v) => { setCampo("con_clave_pais", v); }} error={erroresValidacion.con_clave_pais} />
          <CampoTexto etiqueta="Teléfono" ancho="medio" inputMode="tel" maxLength={LONGITUD_TELEFONO} normaliza={(v) => soloDigitos(v, LONGITUD_TELEFONO)} valor={campo("con_telefono")} onCambio={(v) => { setCampo("con_telefono", v); }} error={erroresValidacion.con_telefono} />
          <CampoTexto etiqueta="Correo" tipo="email" ancho="medio" normaliza={(v) => v.toUpperCase()} valor={campo("con_correo")} onCambio={(v) => { setCampo("con_correo", v); }} error={erroresValidacion.con_correo} />
        </div>
      </div>

      {/* Apartado propio de relación con PEP (0066): reemplaza el checkbox suelto
          del antiguo "Riesgo inicial". Los datos van al borrador `f` y armarDatos
          los normaliza a relacion_pep + pep_*. */}
      <SelectorRelacionPep campo={campo} setCampo={setCampo} errores={erroresValidacion} />

      <button
        style={{ ...st.btnPrim, width: "100%", opacity: puedeGuardar ? 1 : OPACIDAD_GUARDANDO }}
        disabled={!puedeGuardar}
        onClick={() => {
          setGuardando(true);
          void onCrear(armarDatos()).finally(() => { setGuardando(false); });
        }}
      >
        {guardando ? "Guardando…" : demo ? "Crear expediente (demo)" : "Crear expediente"}
      </button>
      {clavesConError.length > 0 && (
        <div style={{ fontSize: 12.5, color: C.amber, background: C.amberBg, borderRadius: 10, padding: "9px 12px", margin: "8px 0 0", lineHeight: 1.4 }}>
          <span style={{ fontWeight: 700 }}>Corrige {clavesConError.length} dato(s)</span> para crear el
          expediente: {clavesConError.map((k) => ETIQUETA_CAMPO_ALTA[k] ?? k).join(" · ")}. Cada campo
          con problema está marcado en rojo, con el ejemplo de lo que espera.
        </div>
      )}
      <p style={{ fontSize: 12, color: C.muted, textAlign: "center", margin: "10px 0 24px" }}>
        Al guardar aparece el cuestionario de documentos según el tipo y el riesgo.
      </p>
    </div>
  );
}
